import { flow, types } from 'mobx-state-tree';
import { Amplify, API, Auth as ClientAuth, Hub, } from 'aws-amplify';
// included via aws-amplify
// eslint-disable-next-line import/no-extraneous-dependencies
import { GRAPHQL_AUTH_MODE, } from '@aws-amplify/api-graphql';
import { getGQLAPI, gqlCall, MainModel, setGQLAPI, STORE_LOAD_FRESH, } from './RootModel';
import { S3FileModel } from './S3FileModel';
import { navStore } from './NavStore';
import { withGuideImageReferences } from './GuideImageTransport';
import { guideRowInPortal, listGuidePortalRows, requireGuidePortal } from './GuidePortalQueries';
import { userInfo, assistantStartGenerateField as assistantStartGenerateFieldGql, assistantGetGenerateFieldResponse as assistantGetGenerateFieldResponseGql, assistantGetCourseTranscripts as assistantGetCourseTranscriptsGql, getConfig as getEnvironmentConfigGQL, getAiDashboard as getAiDashboardGQL, getGeoReporting as getGeoReportingGQL, getGeoGuideDrilldown as getGeoGuideDrilldownGQL, getPageViewStats as getPageViewStatsGQL, getTopProducts as getTopProductsGQL, getConversationSummary as getConversationSummaryGQL, getTopicsHierarchy as getTopicsHierarchyGQL, getSessionDetail as getSessionDetailGQL, getSessionsList as getSessionsListGQL, getConversationsList as getConversationsListGQL, getConversationDetail as getConversationDetailGQL, getEventsForSession as getEventsForSessionGQL, getSessionsGeography as getSessionsGeographyGQL, getPageViewsGeography as getPageViewsGeographyGQL, getPageViewsForUrl as getPageViewsForUrlGQL, getGuide, listGuides, listGuidesByGuideCluster, getGuideCluster, listGuideClusters, listGuideClustersByProposalJob, listInstructors, listShopifyConfigs, } from './aws-temp/graphql/queries';
import { assistantStartThread as assistantStartThreadGql, assistantGetMessages as assistantGetMessagesGql, assistantPostMessage as assistantPostMessageGql, assistantRunThread as assistantRunThreadGql, assistantGetRun as assistantGetRunGql, createGuide as createGuideGql, deleteGuide as deleteGuideGql, deleteGuideCascade as deleteGuideCascadeGql, rebuildGuideBlogIndex as rebuildGuideBlogIndexGql, deleteGuideClusterCascade as deleteGuideClusterCascadeGql, generateGuide as generateGuideGql, scoreGuide as scoreGuideGql, generateGuidesFromUrl as generateGuidesFromUrlGql, startSuggestGuideCitationDomains as startSuggestGuideCitationDomainsGql, continueSuggestGuideCitationDomains as continueSuggestGuideCitationDomainsGql, continueGenerateLlmsTxt as continueGenerateLlmsTxtGql, listGuideShopifyArticleTemplates as listGuideShopifyArticleTemplatesGql, publishInstructorBio as publishInstructorBioGql, publishGuide as publishGuideGql, unpublishGuide as unpublishGuideGql, regenerateGuideTopic as regenerateGuideTopicGql, saveTopicsHierarchy as saveTopicsHierarchyGQL, startGenerateLlmsTxt as startGenerateLlmsTxtGql, updateGuide as updateGuideGql, updateInstructor as updateInstructorGql, createGuideCluster, updateGuideCluster, deleteGuideCluster, startPlanGuideDiscoveryFromUrl, continuePlanGuideDiscoveryFromUrl, auditShopifySitemap as auditShopifySitemapGql, generateShopifyGeoSitemap as generateShopifyGeoSitemapGql, applyShopifyGeoSitemapAsset as applyShopifyGeoSitemapAssetGql, auditShopifyRobots as auditShopifyRobotsGql, applyShopifyRobotsTemplate as applyShopifyRobotsTemplateGql, auditGuideClusterLinks as auditGuideClusterLinksGql, } from './aws-temp/graphql/mutations';
import Schema, { JsonType, setResolver, resolveKey, } from './Schema';
import { pollWithBackoff, settlePromiseWithin, withTimeout, } from './pollWithBackoff';
import { parseAwsJsonObject } from './parseAwsJson';
import { sendTestEmailGql } from './EmailTemplateModel';
import { deployOrganizationGql, getCrawlStatusGQL, getCrawledUrlsGQL, startCrawlGQL, } from './OrganizationModel';
import { deployPortalGql } from './PortalModel';
import { deployRepositoryGql } from './RepositoryModel';
/**
 * Client-side guardrail for long-running guide GraphQL calls (plan / generate-from-URL).
 * Prevents the admin UI from waiting indefinitely; does not describe backend timeout configuration.
 */
const GUIDE_LONG_GRAPHQL_CLIENT_GUARD_MS = 300000 + 15000;
/**
 * Client-side poll window for guide and Core Guide generation (topic + core).
 * 6 minutes — comfortably above the server's 5-minute staleness cap so the
 * client can always observe the terminal server response (200/500) before
 * timing out. After timeout the UI re-enables the Generate button and the user
 * can retry — the Lambda reuses the same responseId so no new job is started.
 */
const GUIDE_GENERATION_CLIENT_POLL_TIMEOUT_MS = 6 * 60 * 1000;
const GUIDE_GENERATION_POLL_TIMEOUT_MS = GUIDE_GENERATION_CLIENT_POLL_TIMEOUT_MS;
const GUIDE_GENERATION_RESOLVER_TIMEOUT_READONLY_MS = 90000;
/**
 * Polling window for generateGuideImage / refineGuideImagePreview after 102 kickoff.
 * Uses the same client guard as guide generation so the editor does not hang on a single request.
 */
const GUIDE_IMAGE_POLL_TIMEOUT_MS = GUIDE_LONG_GRAPHQL_CLIENT_GUARD_MS;
/**
 * Total wall time allowed for polling continuePlanGuideDiscoveryFromUrl after start.
 * Default pollWithBackoff (180s) is too short for slow OpenAI background web_search jobs.
 */
const GUIDE_DISCOVERY_POLL_TIMEOUT_MS = 600000;
const LLMS_TXT_POLL_TIMEOUT_MS = GUIDE_DISCOVERY_POLL_TIMEOUT_MS;
const GUIDE_CITATION_SUGGESTIONS_POLL_TIMEOUT_MS = GUIDE_DISCOVERY_POLL_TIMEOUT_MS;
let Auth;
const AdminModel = types.model('AdminModel', {
    assistantSendDisabled: types.optional(types.boolean, false),
    environmentConfig: types.frozen(JsonType),
    reportStartDate: types.maybe(types.string),
    reportEndDate: types.maybe(types.string),
    reportTimezone: types.maybe(types.string),
}).actions((self) => {
    // See EditWrapper and WysiwygInPlaceEditor for how this is used.
    let inWysiwygEditorSelect = false;
    // tracks items that need to be saved
    const dirtyItems = [];
    const addItemToSave = (item) => {
        if (!dirtyItems.includes(item)) {
            dirtyItems.push(item);
        }
    };
    const getItemsToSave = () => dirtyItems;
    const saveAll = () => {
        console.log('Save ALL', dirtyItems);
        const results = dirtyItems.map((item) => {
            if (item.syncState === 'not_found') {
                return self.add('learningBoard', item);
            }
            return self.update('learningBoard', item);
        });
        dirtyItems.length = 0;
        return results;
    };
    const sendTestEmail = flow(function* sendTestEmail(subject, body, fromEmail, toEmail, bccEmails) {
        const result = yield getGQLAPI().graphql({
            query: sendTestEmailGql,
            variables: {
                organizationID: self.currentOrganization?.id,
                portalID: self.currentPortal?.id,
                customerID: self.currentCustomer?.id,
                subject,
                body: JSON.stringify(body),
                fromEmail,
                toEmail,
                bccEmails,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result.data || !result.data.sendTestEmail) {
            throw Error('no data returned');
        }
        // const { subjectResult, bodyResult } = result.data.sendTestEmail;
        return result.data.sendTestEmail;
    });
    const deployOrganization = flow(function* deployOrganization(organization) {
        const result = yield getGQLAPI().graphql({
            query: deployOrganizationGql,
            variables: {
                organizationID: organization?.id,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result.data || !result.data.deployOrganization) {
            throw Error('no data returned');
        }
        return result.data.deployOrganization;
    });
    const deployPortal = flow(function* deployPortal(portal) {
        const result = yield getGQLAPI().graphql({
            query: deployPortalGql,
            variables: {
                organizationID: portal.organization?.id,
                portalID: portal.id,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result.data || !result.data.deployPortal) {
            throw Error('no data returned');
        }
        return result.data.deployPortal;
    });
    const deployRepository = flow(function* deployRepository(repository) {
        const result = yield getGQLAPI().graphql({
            query: deployRepositoryGql,
            variables: {
                organizationID: repository.organization?.id,
                repositoryID: repository.id,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result.data || !result.data.deployRepository) {
            throw Error('no data returned');
        }
        return result.data.deployRepository;
    });
    const assistantStartThread = async (itemType, itemID) => {
        const result = await getGQLAPI().graphql({
            query: assistantStartThreadGql,
            variables: {
                organizationID: self.currentOrganization?.id,
                itemType,
                itemID,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result.data || !result.data.assistantStartThread) {
            throw Error('no data returned');
        }
        return result.data.assistantStartThread;
    };
    const assistantGetMessages = async (itemType, itemID, threadID) => {
        const result = await getGQLAPI().graphql({
            query: assistantGetMessagesGql,
            variables: {
                organizationID: self.currentOrganization?.id,
                itemType,
                itemID,
                threadID,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result.data || !result.data.assistantGetMessages) {
            throw Error('no data returned');
        }
        return result.data.assistantGetMessages;
    };
    const assistantPostMessage = async (itemType, itemID, threadID, content) => {
        const result = await getGQLAPI().graphql({
            query: assistantPostMessageGql,
            variables: {
                organizationID: self.currentOrganization?.id,
                itemType,
                itemID,
                threadID,
                content,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result.data || !result.data.assistantPostMessage) {
            throw Error('no data returned');
        }
        return result.data.assistantPostMessage;
    };
    const assistantRunThread = async (itemType, itemID, threadID) => {
        const result = await getGQLAPI().graphql({
            query: assistantRunThreadGql,
            variables: {
                organizationID: self.currentOrganization?.id,
                itemType,
                itemID,
                threadID,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result.data || !result.data.assistantRunThread) {
            throw Error('no data returned');
        }
        return result.data.assistantRunThread;
    };
    const assistantGetRun = async (itemType, itemID, threadID, runID) => {
        const result = await getGQLAPI().graphql({
            query: assistantGetRunGql,
            variables: {
                organizationID: self.currentOrganization?.id,
                itemType,
                itemID,
                threadID,
                runID,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result.data || !result.data.assistantGetRun) {
            throw Error('no data returned');
        }
        return result.data.assistantGetRun;
    };
    const assistantStartGenerateField = async (itemType, itemKey, prompt) => {
        const result = await getGQLAPI().graphql({
            query: assistantStartGenerateFieldGql,
            variables: {
                organizationID: self.currentOrganization?.id,
                itemType,
                itemKey,
                prompt,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.assistantStartGenerateField) {
            throw Error('no data returned');
        }
        return result.data.assistantStartGenerateField;
    };
    const assistantGetGenerateFieldResponse = async (threadID, runID) => {
        const result = await getGQLAPI().graphql({
            query: assistantGetGenerateFieldResponseGql,
            variables: {
                threadID,
                runID,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.assistantGetGenerateFieldResponse) {
            throw Error('no data returned');
        }
        return result.data.assistantGetGenerateFieldResponse;
    };
    const assistantGetCourseTranscripts = async (organizationID, id) => {
        const result = await getGQLAPI().graphql({
            query: assistantGetCourseTranscriptsGql,
            variables: {
                organizationID,
                id,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.assistantGetCourseTranscripts) {
            throw new Error('No data returned from assistantGetCourseTranscripts.');
        }
        return result.data.assistantGetCourseTranscripts;
    };
    const isExpectedLongRunningGraphqlError = (error) => {
        const message = String(error?.message
            || error?.errors?.[0]?.message
            || error
            || '').toLowerCase();
        return [
            'timeout',
            'timed out',
            'network error',
            'failed to fetch',
            'request aborted',
            'aborted',
            'socket hang up',
            '504',
            '503',
        ].some((pattern) => message.includes(pattern));
    };
    const normalizeGuideSourceUrl = (value) => String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\/+$/g, '');
    const sortGuidesForAdmin = (guides) => [...(guides || [])].sort((a, b) => {
        const latestB = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
        const latestA = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
        return latestB - latestA;
    });
    const getGuideListDelta = (baselineGuides, currentGuides, sourceUrl) => {
        const normalizedSourceUrl = normalizeGuideSourceUrl(sourceUrl);
        const baselineMap = new Map((baselineGuides || []).map((guide) => [guide.id, guide.updatedAt || guide.createdAt || '']));
        return (currentGuides || []).filter((guide) => {
            if (normalizedSourceUrl) {
                const guideSourceUrl = normalizeGuideSourceUrl(guide?.sourceUrl);
                if (guideSourceUrl !== normalizedSourceUrl) {
                    return false;
                }
            }
            const baselineTimestamp = baselineMap.get(guide.id);
            if (!baselineTimestamp) {
                return true;
            }
            const currentTimestamp = guide.updatedAt || guide.createdAt || '';
            return Boolean(currentTimestamp && currentTimestamp !== baselineTimestamp);
        });
    };
    const getGuideGenerationCompletionFromRecord = ({ baselineGuide, currentGuide, operation, }) => {
        if (!currentGuide)
            return null;
        const currentUpdatedAt = currentGuide.updatedAt || '';
        const baselineUpdatedAt = baselineGuide?.updatedAt || '';
        const currentLastGeneratedAt = currentGuide.lastGeneratedAt || '';
        const baselineLastGeneratedAt = baselineGuide?.lastGeneratedAt || '';
        const recordChanged = Boolean((currentUpdatedAt && currentUpdatedAt !== baselineUpdatedAt)
            || (currentLastGeneratedAt && currentLastGeneratedAt !== baselineLastGeneratedAt));
        if (!recordChanged)
            return null;
        const generationStatus = String(currentGuide.generationStatus || '').toLowerCase();
        if (generationStatus === 'generating' || generationStatus === 'queued')
            return null;
        if (generationStatus === 'failed') {
            return {
                statusCode: 500,
                guideId: currentGuide.id,
                message: 'Guide generation failed',
            };
        }
        const guideStatus = String(currentGuide.status || '').toLowerCase();
        const contentGenerated = operation === 'content'
            && (guideStatus === 'draft'
                || guideStatus === 'published'
                || Boolean(currentLastGeneratedAt && currentLastGeneratedAt !== baselineLastGeneratedAt));
        const topicGenerated = operation === 'topic'
            && Boolean(currentLastGeneratedAt && currentLastGeneratedAt !== baselineLastGeneratedAt);
        if (!contentGenerated && !topicGenerated)
            return null;
        return {
            statusCode: 200,
            guideId: currentGuide.id,
            message: operation === 'content'
                ? 'Guide generated successfully'
                : 'Guide topics regenerated successfully',
        };
    };
    const getGraphqlErrorList = (error) => (Array.isArray(error?.errors)
        ? error.errors
        : Array.isArray(error?.data?.errors)
            ? error.data.errors
            : []);
    const isLambdaExecutionTimeoutError = (error) => {
        const graphqlErrors = getGraphqlErrorList(error);
        const combinedMessage = [
            error?.message,
            ...graphqlErrors.map((entry) => `${entry?.errorType || ''} ${entry?.message || ''}`),
        ].join(' ').toLowerCase();
        return combinedMessage.includes('lambda:executiontimeoutexception')
            || combinedMessage.includes('execution timed out');
    };
    const isTransientOpenAiGuideGenerationError = (...values) => {
        const combinedMessage = values.map((value) => {
            if (!value)
                return '';
            if (typeof value === 'string')
                return value;
            return [
                value.message,
                value.error,
                value.generationErrorMessage,
                value.guide?.generationErrorMessage,
            ].filter(Boolean).join(' ');
        }).join(' ').toLowerCase();
        return /wfr_[a-z0-9]+/i.test(combinedMessage)
            || combinedMessage.includes('an error occurred while processing your request')
            || combinedMessage.includes('please include the request id')
            || combinedMessage.includes('help.openai.com');
    };
    const generateField = flow(function* getGenerateFieldFunction(itemType, itemKey, prompt) {
        try {
            if (!self.currentOrganization) {
                throw Error('currentOrganazation cannot be empty');
            }
            console.log('generateAI called');
            let response = yield assistantStartGenerateField(itemType, itemKey, prompt);
            if (response.statusCode === 102) {
                const pollResult = yield pollWithBackoff({
                    poll: async () => {
                        const nextResponse = await assistantGetGenerateFieldResponse(response.threadID, response.runID);
                        console.log('run response', nextResponse);
                        return {
                            done: nextResponse.statusCode !== 102,
                            value: nextResponse,
                        };
                    },
                });
                response = pollResult.value;
            }
            if (response.statusCode === 200) {
                return response.body;
            }
        }
        catch (error) {
            console.log('Error:', error);
        }
        return undefined;
    });
    const getGuideRecord = async (organizationID, id, portalID = self.currentPortal?.id) => {
        const selectedPortal = requireGuidePortal(portalID);
        const result = await getGQLAPI().graphql({
            query: getGuide,
            variables: { organizationID, id },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        const row = result?.data?.getGuide;
        return guideRowInPortal(row, organizationID, selectedPortal) ? row : null;
    };
    /** Portal filtering precedes the result cap, including single-page callers. */
    const listGuidesForAdmin = async (organizationID, limit = 100, guideClusterID, aggregateMax, portalID = self.currentPortal?.id) => {
        const selectedPortal = requireGuidePortal(portalID);
        if (guideClusterID && !await getGuideClusterRecord(organizationID, guideClusterID, selectedPortal))
            return [];
        const cap = Math.max(1, Math.min(10000, aggregateMax ?? limit));
        const field = guideClusterID ? 'listGuidesByGuideCluster' : 'listGuides';
        return listGuidePortalRows(async (nextToken) => {
            const result = await getGQLAPI().graphql({
                query: guideClusterID ? listGuidesByGuideCluster : listGuides,
                variables: {
                    organizationID,
                    ...(guideClusterID ? { guideClusterID: { eq: guideClusterID } } : {}),
                    filter: { portalID: { eq: selectedPortal } },
                    limit: Math.max(1, Math.min(200, limit)),
                    nextToken,
                },
                authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
            });
            return result?.data?.[field];
        }, organizationID, selectedPortal, cap);
    };
    /** Legacy editorial rows still need explicit portal ownership. */
    const guideClusterEditorialFilter = {
        or: [
            { recordType: { eq: 'cluster' } },
            { recordType: { attributeExists: false } },
        ],
    };
    const listGuideClustersForAdmin = async (organizationID, limit = 200, portalID = self.currentPortal?.id) => {
        const selectedPortal = requireGuidePortal(portalID);
        return listGuidePortalRows(async (nextToken) => {
            const result = await getGQLAPI().graphql({
                query: listGuideClusters,
                variables: {
                    organizationID,
                    limit: Math.min(200, Math.max(1, limit)),
                    nextToken,
                    filter: { and: [guideClusterEditorialFilter, { portalID: { eq: selectedPortal } }] },
                },
                authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
            });
            return result?.data?.listGuideClusters;
        }, organizationID, selectedPortal, Math.max(1, limit));
    };
    const readGuideClusterForRelationshipGuard = async (organizationID, id) => {
        const result = await getGQLAPI().graphql({
            query: getGuideCluster,
            variables: { organizationID, id },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        return result?.data?.getGuideCluster ?? null;
    };
    const getGuideClusterRecord = async (organizationID, id, portalID = self.currentPortal?.id) => {
        const selectedPortal = requireGuidePortal(portalID);
        const row = await readGuideClusterForRelationshipGuard(organizationID, id);
        return guideRowInPortal(row, organizationID, selectedPortal) ? row : null;
    };
    const createGuideClusterRecord = async (input) => {
        const result = await getGQLAPI().graphql({
            query: createGuideCluster,
            variables: { input },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.createGuideCluster) {
            throw Error('no data returned');
        }
        return result.data.createGuideCluster;
    };
    const updateGuideClusterRecord = async (input) => {
        const result = await getGQLAPI().graphql({
            query: updateGuideCluster,
            variables: { input },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.updateGuideCluster) {
            throw Error('no data returned');
        }
        return result.data.updateGuideCluster;
    };
    const deleteGuideClusterRecord = async (input) => {
        const result = await getGQLAPI().graphql({
            query: deleteGuideCluster,
            variables: { input },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.deleteGuideCluster) {
            throw Error('no data returned');
        }
        return result.data.deleteGuideCluster;
    };
    const listGuideDiscoveryProposalRowsForJob = async (organizationID, jobId, limit = 200) => {
        const items = [];
        let nextToken;
        const perPage = Math.min(200, Math.max(1, limit));
        while (items.length < limit) {
            const result = await getGQLAPI().graphql({
                query: listGuideClustersByProposalJob,
                variables: {
                    organizationID,
                    proposalJobId: { eq: jobId },
                    filter: { recordType: { eq: 'proposalRow' } },
                    limit: Math.min(perPage, limit - items.length),
                    nextToken: nextToken || undefined,
                },
                authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
            });
            const batch = result?.data?.listGuideClustersByProposalJob?.items || [];
            items.push(...batch);
            nextToken = result?.data?.listGuideClustersByProposalJob?.nextToken ?? null;
            if (!nextToken)
                break;
        }
        const trimmed = items.slice(0, limit);
        return [...trimmed].sort((a, b) => (Number(a?.sortOrder) || 0) - (Number(b?.sortOrder) || 0));
    };
    const updateGuideDiscoveryProposalRowRecord = async (input) => {
        const result = await getGQLAPI().graphql({
            query: updateGuideCluster,
            variables: { input },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.updateGuideCluster) {
            throw Error('no data returned');
        }
        return result.data.updateGuideCluster;
    };
    const createGuideRecord = async (input) => {
        const result = await getGQLAPI().graphql({
            query: createGuideGql,
            variables: { input },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.createGuide) {
            throw Error('no data returned');
        }
        return result.data.createGuide;
    };
    /**
     * MCN-2208: create GuideCluster rows and stub Guides (client-side bulk, until dedicated discovery Lambda).
     */
    const createClustersAndStubGuidesFromRows = async (params) => {
        const normalizeClusterNameKey = (value) => String(value || '')
            .trim()
            .toLowerCase()
            .replace(/\s+/g, ' ');
        const normalizeGuideStubSlug = (value, fallback) => (String(value || '')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            || fallback);
        const { organizationID, portalID, sourceUrl, strategyLevel = 'foundation', rows, } = params;
        const createdClusterIds = [];
        const createdRowIndexes = [];
        let guidesCreated = 0;
        const skippedClusterNames = [];
        const failedClusterNames = [];
        const existingClusters = await listGuideClustersForAdmin(organizationID, 500, portalID);
        const seenClusterKeys = new Set((existingClusters || [])
            .map((cluster) => normalizeClusterNameKey(cluster?.name))
            .filter(Boolean));
        for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
            const row = rows[rowIndex];
            const trimmedClusterName = row.clusterName?.trim();
            if (!trimmedClusterName) {
                // eslint-disable-next-line no-continue
                continue;
            }
            const clusterNameKey = normalizeClusterNameKey(trimmedClusterName);
            if (!clusterNameKey || seenClusterKeys.has(clusterNameKey)) {
                skippedClusterNames.push(trimmedClusterName);
                // eslint-disable-next-line no-continue
                continue;
            }
            const normalizedDescription = typeof row.description === 'string'
                ? row.description.trim()
                : '';
            try {
                const cluster = await createGuideClusterRecord({
                    organizationID,
                    recordType: 'cluster',
                    portalID,
                    name: trimmedClusterName,
                    description: normalizedDescription || undefined,
                    discoverySourceUrl: sourceUrl || undefined,
                    strategyTags: [strategyLevel],
                });
                if (!cluster?.id) {
                    throw new Error('createGuideCluster did not return id');
                }
                createdClusterIds.push(cluster.id);
                createdRowIndexes.push(rowIndex);
                seenClusterKeys.add(clusterNameKey);
                const n = Math.max(0, Math.min(50, Number(row.guidesNeeded) || 0));
                const guideTopics = Array.isArray(row.guideTopics)
                    ? row.guideTopics
                        .map((item) => String(item || '').trim())
                        .filter(Boolean)
                    : [];
                for (let i = 0; i < n; i += 1) {
                    const suggestedTopic = guideTopics[i] || `${trimmedClusterName} — guide ${i + 1}`;
                    const guideSlug = normalizeGuideStubSlug(suggestedTopic, `guide-${i + 1}`);
                    const guideExcerpt = normalizedDescription
                        ? `${suggestedTopic}. ${normalizedDescription}`
                        : `Guide covering ${suggestedTopic}.`;
                    await createGuideRecord({
                        organizationID,
                        portalID,
                        guideClusterID: cluster.id,
                        title: suggestedTopic,
                        topic: suggestedTopic,
                        slug: guideSlug,
                        excerpt: guideExcerpt,
                        status: 'stub',
                        sourceUrl: sourceUrl || undefined,
                    });
                    guidesCreated += 1;
                }
            }
            catch (error) {
                failedClusterNames.push(trimmedClusterName);
            }
        }
        if (createdClusterIds.length === 0 && failedClusterNames.length > 0) {
            throw new Error(`Failed to create selected clusters: ${failedClusterNames.join(', ')}`);
        }
        return {
            createdClusterIds,
            createdRowIndexes,
            guidesCreated,
            skippedClusterNames: [...new Set(skippedClusterNames)],
            failedClusterNames: [...new Set(failedClusterNames)],
        };
    };
    /**
     * Update a Guide row. Pass `options.expectedUpdatedAt` (the exact AWSDateTime string
     * last read from the server) to make the write conditional: AppSync then rejects it
     * with `DynamoDB:ConditionalCheckFailedException` if the row changed since.
     * Without it the write is unconditional (last write wins), as before.
     */
    const updateGuideRecord = async (input, options) => {
        const currentGuide = await getGuideRecord(String(input.organizationID), String(input.id));
        if (!currentGuide)
            throw Error('Guide not found in this portal');
        if (input.portalID !== undefined && input.portalID !== self.currentPortal?.id)
            throw Error('Cannot move a guide to another portal');
        const patchesParent = Object.prototype.hasOwnProperty.call(input, 'guideClusterID');
        const currentParentID = currentGuide.guideClusterID == null ? null : String(currentGuide.guideClusterID);
        const nextParentID = input.guideClusterID == null || input.guideClusterID === ''
            ? null
            : String(input.guideClusterID);
        if (patchesParent
            && nextParentID !== currentParentID
            && nextParentID
            && !await getGuideClusterRecord(String(input.organizationID), nextParentID))
            throw Error('Cluster not found in this portal');
        const payload = { ...input };
        if (payload.content != null
            && typeof payload.content === 'object'
            && !Array.isArray(payload.content)) {
            payload.content = JSON.stringify(payload.content);
        }
        // Send the server string as-is: re-formatting through Date can change the
        // fractional seconds and fail the equality check (see awsDateTimeForCondition).
        const expectedUpdatedAt = options?.expectedUpdatedAt?.trim();
        const result = await getGQLAPI().graphql({
            query: updateGuideGql,
            variables: {
                input: payload,
                ...(expectedUpdatedAt ? { condition: { updatedAt: { eq: expectedUpdatedAt } } } : {}),
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.updateGuide) {
            throw Error('no data returned');
        }
        return result.data.updateGuide;
    };
    const deleteGuideRecord = async (input) => {
        const result = await getGQLAPI().graphql({
            query: deleteGuideGql,
            variables: { input },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.deleteGuide) {
            throw Error('no data returned');
        }
        return result.data.deleteGuide;
    };
    const deleteGuideCascade = async (organizationID, guideId) => {
        const result = await getGQLAPI().graphql({
            query: deleteGuideCascadeGql,
            variables: { organizationID, guideId },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.deleteGuideCascade) {
            throw Error('no data returned');
        }
        return result.data.deleteGuideCascade;
    };
    const rebuildGuideBlogIndex = async (organizationID, syncConfigID) => {
        const result = await getGQLAPI().graphql({
            query: rebuildGuideBlogIndexGql,
            variables: { organizationID, syncConfigID },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.rebuildGuideBlogIndex) {
            throw Error('no data returned');
        }
        return result.data.rebuildGuideBlogIndex;
    };
    const generateGuide = async (variables) => {
        const guide = await getGuideRecord(variables.organizationID, variables.guideId);
        if (!guide)
            throw Error('Guide not found in this portal');
        if (guide.guideClusterID) {
            // Generation consumes the already-scoped Guide, not its cluster row.
            // Preserve the cross-portal guard when the parent exists, but do not
            // lock historical stubs whose deleted parent cannot be resolved.
            const selectedPortal = requireGuidePortal(self.currentPortal?.id);
            const existingParent = await readGuideClusterForRelationshipGuard(variables.organizationID, String(guide.guideClusterID));
            if (existingParent && !guideRowInPortal(existingParent, variables.organizationID, selectedPortal)) {
                throw Error('Cluster not found in this portal');
            }
        }
        const result = await getGQLAPI().graphql({
            query: generateGuideGql,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.generateGuide) {
            const error = Error('no data returned');
            if (Array.isArray(result?.errors)) {
                error.errors = result.errors;
            }
            throw error;
        }
        return result.data.generateGuide;
    };
    /**
     * Scores a guide's content quality (SEO / GEO / E-E-A-T / AI-extractability).
     * The Lambda persists the result onto the Guide and returns the full
     * breakdown. `contentFingerprint` is stored verbatim so the editor can
     * detect staleness across reloads.
     */
    const scoreGuide = async (variables) => {
        const result = await getGQLAPI().graphql({
            query: scoreGuideGql,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        const scored = result?.data?.scoreGuide;
        if (!scored) {
            throw Error('no data returned');
        }
        if (scored.statusCode && scored.statusCode >= 400) {
            throw Error(scored.error || scored.message || 'Guide scoring failed');
        }
        return scored;
    };
    const startSuggestGuideCitationDomains = async (variables) => {
        const result = await withTimeout(getGQLAPI().graphql({
            query: startSuggestGuideCitationDomainsGql,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        }), GUIDE_LONG_GRAPHQL_CLIENT_GUARD_MS, 'Trusted source suggestions timed out while starting. Try again, or contact support if this persists.');
        if (!result?.data?.startSuggestGuideCitationDomains) {
            throw Error('no data returned');
        }
        return result.data.startSuggestGuideCitationDomains;
    };
    const continueSuggestGuideCitationDomains = async (variables) => {
        const result = await withTimeout(getGQLAPI().graphql({
            query: continueSuggestGuideCitationDomainsGql,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        }), GUIDE_LONG_GRAPHQL_CLIENT_GUARD_MS, 'Trusted source suggestions poll timed out waiting for a response. Try again, or contact support if this persists.');
        if (!result?.data?.continueSuggestGuideCitationDomains) {
            throw Error('no data returned');
        }
        return result.data.continueSuggestGuideCitationDomains;
    };
    const suggestGuideCitationDomains = async (variables) => {
        let response = await startSuggestGuideCitationDomains(variables);
        const jobId = response?.jobId;
        if (response?.statusCode !== 102 || !jobId) {
            return response;
        }
        const pollResult = await pollWithBackoff({
            timeoutMs: GUIDE_CITATION_SUGGESTIONS_POLL_TIMEOUT_MS,
            poll: async () => {
                response = await continueSuggestGuideCitationDomains({
                    organizationID: variables.organizationID,
                    jobId,
                });
                return {
                    done: response?.statusCode !== 102,
                    value: response,
                };
            },
        });
        if (pollResult.status === 'timeout') {
            throw new Error(`Trusted source suggestions are still processing on the server. Wait a moment and try again. Job: ${jobId}`);
        }
        return pollResult.value;
    };
    const auditShopifySitemap = async (variables) => {
        const result = await withTimeout(getGQLAPI().graphql({
            query: auditShopifySitemapGql,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        }), GUIDE_LONG_GRAPHQL_CLIENT_GUARD_MS, 'Sitemap audit timed out.');
        if (!result?.data?.auditShopifySitemap)
            throw Error('no data returned');
        return result.data.auditShopifySitemap;
    };
    const generateShopifyGeoSitemap = async (variables) => {
        const result = await withTimeout(getGQLAPI().graphql({
            query: generateShopifyGeoSitemapGql,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        }), GUIDE_LONG_GRAPHQL_CLIENT_GUARD_MS, 'GEO sitemap generation timed out.');
        if (!result?.data?.generateShopifyGeoSitemap)
            throw Error('no data returned');
        return result.data.generateShopifyGeoSitemap;
    };
    const applyShopifyGeoSitemapAsset = async (variables) => {
        const result = await withTimeout(getGQLAPI().graphql({
            query: applyShopifyGeoSitemapAssetGql,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        }), GUIDE_LONG_GRAPHQL_CLIENT_GUARD_MS, 'GEO sitemap publish timed out.');
        if (!result?.data?.applyShopifyGeoSitemapAsset)
            throw Error('no data returned');
        return result.data.applyShopifyGeoSitemapAsset;
    };
    const auditShopifyRobots = async (variables) => {
        const result = await withTimeout(getGQLAPI().graphql({
            query: auditShopifyRobotsGql,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        }), GUIDE_LONG_GRAPHQL_CLIENT_GUARD_MS, 'Robots audit timed out.');
        if (!result?.data?.auditShopifyRobots)
            throw Error('no data returned');
        return result.data.auditShopifyRobots;
    };
    const applyShopifyRobotsTemplate = async (variables) => {
        const result = await withTimeout(getGQLAPI().graphql({
            query: applyShopifyRobotsTemplateGql,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        }), GUIDE_LONG_GRAPHQL_CLIENT_GUARD_MS, 'Robots template publish timed out.');
        if (!result?.data?.applyShopifyRobotsTemplate)
            throw Error('no data returned');
        return result.data.applyShopifyRobotsTemplate;
    };
    const generateCoreGuideMutation = /* GraphQL */ `mutation GenerateCoreGuide(
      $organizationID: ID!
      $clusterID: ID!
      $sourceUrl: String
      $contentPromptOverride: String
      $additionalInstructions: String
      $optimizationMode: String
      $bylineInstructorID: ID
      $reviewerInstructorID: ID
    ) {
      generateCoreGuide(
        organizationID: $organizationID
        clusterID: $clusterID
        sourceUrl: $sourceUrl
        contentPromptOverride: $contentPromptOverride
        additionalInstructions: $additionalInstructions
        optimizationMode: $optimizationMode
        bylineInstructorID: $bylineInstructorID
        reviewerInstructorID: $reviewerInstructorID
      ) {
        statusCode
        guideId
        clusterId
        message
        error
      }
    }`;
    const generateCoreGuide = async (variables) => {
        if (!await getGuideClusterRecord(variables.organizationID, variables.clusterID))
            throw Error('Cluster not found in this portal');
        const result = await getGQLAPI().graphql({
            query: generateCoreGuideMutation,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.generateCoreGuide) {
            throw Error('no data returned');
        }
        return result.data.generateCoreGuide;
    };
    const regenerateGuideTopic = async (variables) => {
        const result = await getGQLAPI().graphql({
            query: regenerateGuideTopicGql,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.regenerateGuideTopic) {
            throw Error('no data returned');
        }
        return result.data.regenerateGuideTopic;
    };
    const generateGuidesFromUrl = async (variables) => {
        const result = await withTimeout(getGQLAPI().graphql({
            query: generateGuidesFromUrlGql,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        }), GUIDE_LONG_GRAPHQL_CLIENT_GUARD_MS, 'Guide generation from URL timed out waiting for a response. The server may still be processing—refresh Guide Inventory in a moment or try again.');
        if (!result?.data?.generateGuidesFromUrl) {
            throw Error('no data returned');
        }
        return result.data.generateGuidesFromUrl;
    };
    const generateLlmsTxt = async (variables) => {
        const startResult = await withTimeout(getGQLAPI().graphql({
            query: startGenerateLlmsTxtGql,
            variables: {
                organizationID: variables.organizationID,
                portalID: variables.portalID ?? null,
                siteUrl: variables.siteUrl,
                companyName: variables.companyName ?? null,
                description: variables.description ?? null,
                context: variables.context ?? null,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        }), GUIDE_LONG_GRAPHQL_CLIENT_GUARD_MS, 'llms.txt generation timed out while starting. Try again, or contact support if this persists.');
        let response = startResult?.data?.startGenerateLlmsTxt;
        if (!response) {
            throw Error('no data returned');
        }
        const jobId = response?.jobId;
        if (response?.statusCode !== 102 || !jobId) {
            return response;
        }
        const pollResult = await pollWithBackoff({
            timeoutMs: LLMS_TXT_POLL_TIMEOUT_MS,
            poll: async () => {
                const cont = await withTimeout(getGQLAPI().graphql({
                    query: continueGenerateLlmsTxtGql,
                    variables: {
                        organizationID: variables.organizationID,
                        jobId,
                    },
                    authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
                }), GUIDE_LONG_GRAPHQL_CLIENT_GUARD_MS, 'llms.txt generation poll timed out waiting for a response. Try again, or contact support if this persists.');
                response = cont?.data?.continueGenerateLlmsTxt;
                return {
                    done: response?.statusCode !== 102,
                    value: response,
                };
            },
        });
        if (pollResult.status === 'timeout') {
            throw new Error(`llms.txt generation is still processing on the server. Wait a moment and try again. Job: ${jobId}`);
        }
        return pollResult.value;
    };
    const continueGuideDiscoveryPlan = async (organizationID, jobId) => {
        const cont = await withTimeout(getGQLAPI().graphql({
            query: continuePlanGuideDiscoveryFromUrl,
            variables: {
                organizationID,
                jobId,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        }), GUIDE_LONG_GRAPHQL_CLIENT_GUARD_MS, 'Discovery plan poll timed out waiting for a response. Try again, or contact support if this persists.');
        return cont?.data?.continuePlanGuideDiscoveryFromUrl;
    };
    const attachGuideDiscoveryProposalRows = async (organizationID, jobId, response) => {
        if (response?.statusCode !== 200)
            return response;
        const proposalRows = await listGuideDiscoveryProposalRowsForJob(organizationID, jobId);
        return {
            ...response,
            proposalRows,
        };
    };
    const pollGuideDiscoveryPlanToCompletion = async (organizationID, jobId) => {
        const pollResult = await pollWithBackoff({
            timeoutMs: GUIDE_DISCOVERY_POLL_TIMEOUT_MS,
            poll: async () => {
                const next = await continueGuideDiscoveryPlan(organizationID, jobId);
                return {
                    done: next?.statusCode !== 102,
                    value: next,
                };
            },
        });
        if (pollResult.status === 'timeout') {
            throw new Error('Discovery plan is still processing on the server. Wait a moment and run plan again, or contact support if this persists.');
        }
        return attachGuideDiscoveryProposalRows(organizationID, jobId, pollResult.value);
    };
    const startGuideDiscoveryPlan = async (variables) => {
        const gqlVars = {
            organizationID: variables.organizationID,
            portalID: variables.portalID ?? null,
            url: variables.url,
            strategyLevel: variables.strategyLevel ?? null,
            additionalInstructions: variables.additionalInstructions ?? null,
            previousJobId: variables.previousJobId ?? null,
            refinementNotes: variables.refinementNotes ?? null,
        };
        const startResult = await withTimeout(getGQLAPI().graphql({
            query: startPlanGuideDiscoveryFromUrl,
            variables: gqlVars,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        }), GUIDE_LONG_GRAPHQL_CLIENT_GUARD_MS, 'Discovery plan timed out waiting for a response. The server may still be processing—try again, or contact support if this persists.');
        if (!startResult?.data?.startPlanGuideDiscoveryFromUrl) {
            throw Error('no data returned');
        }
        return startResult.data.startPlanGuideDiscoveryFromUrl;
    };
    const resumeGuideDiscoveryPlan = async (variables) => {
        const response = await continueGuideDiscoveryPlan(variables.organizationID, variables.jobId);
        if (response?.statusCode === 102) {
            return pollGuideDiscoveryPlanToCompletion(variables.organizationID, variables.jobId);
        }
        return attachGuideDiscoveryProposalRows(variables.organizationID, variables.jobId, response);
    };
    const planGuideDiscoveryFromUrl = async (variables) => {
        let response = await startGuideDiscoveryPlan(variables);
        const jobId = response?.jobId;
        if (response?.statusCode === 102 && jobId) {
            response = await pollGuideDiscoveryPlanToCompletion(variables.organizationID, jobId);
        }
        if (response?.statusCode === 200 && jobId) {
            return attachGuideDiscoveryProposalRows(variables.organizationID, jobId, response);
        }
        return response;
    };
    const publishGuide = async (organizationID, guideId) => {
        const result = await getGQLAPI().graphql({
            query: publishGuideGql,
            variables: {
                organizationID,
                guideId,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.publishGuide) {
            throw Error('no data returned');
        }
        return result.data.publishGuide;
    };
    const unpublishGuide = async (organizationID, guideId) => {
        const result = await getGQLAPI().graphql({
            query: unpublishGuideGql,
            variables: {
                organizationID,
                guideId,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.unpublishGuide) {
            throw Error('no data returned');
        }
        return result.data.unpublishGuide;
    };
    const deleteGuideClusterCascade = async (variables) => {
        const result = await getGQLAPI().graphql({
            query: deleteGuideClusterCascadeGql,
            variables: {
                organizationID: variables.organizationID,
                clusterId: variables.clusterId,
                targetClusterId: variables.targetClusterId || null,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.deleteGuideClusterCascade) {
            throw Error('no data returned');
        }
        return result.data.deleteGuideClusterCascade;
    };
    const publishInstructorBio = async (variables) => {
        const result = await getGQLAPI().graphql({
            query: publishInstructorBioGql,
            variables: {
                organizationID: variables.organizationID,
                instructorID: variables.instructorID,
                syncConfigID: variables.syncConfigID || null,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        const response = result?.data?.publishInstructorBio;
        if (!response) {
            throw Error('no data returned');
        }
        if (response.statusCode && response.statusCode >= 400) {
            throw Error(response.error || response.message || 'Failed to publish instructor bio');
        }
        return response;
    };
    /**
     * Lists Shopify article templates for the publish target store.
     * Admin UI must use this (or other AdminStore methods), not raw GraphQL in components.
     */
    const listGuideShopifyArticleTemplates = async (organizationID, syncConfigID) => {
        const result = await getGQLAPI().graphql({
            query: listGuideShopifyArticleTemplatesGql,
            variables: {
                organizationID,
                syncConfigID: syncConfigID || null,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        const response = result?.data?.listGuideShopifyArticleTemplates;
        if (!response) {
            throw Error('no data returned');
        }
        if (response.statusCode && response.statusCode >= 400) {
            throw Error(response.error || response.message || 'Failed to list Shopify article templates');
        }
        return Array.isArray(response.templates) ? response.templates : [];
    };
    /**
     * Active ShopifyConfig rows for an org (for guide publish / multi-store guardrails).
     */
    const listActiveShopifyConfigsForOrganization = async (organizationID) => {
        const orgId = String(organizationID || '').trim();
        if (!orgId) {
            return [];
        }
        const result = await getGQLAPI().graphql({
            query: listShopifyConfigs,
            variables: {
                organizationID: orgId,
                limit: 100,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        const items = result?.data?.listShopifyConfigs?.items ?? [];
        return items.filter((c) => String(c?.organizationID || '') === orgId
            && String(c?.status || '').toLowerCase() === 'active');
    };
    const listAiVisibilityInstructorsForOrganization = async (organizationID) => {
        const orgId = String(organizationID || '').trim();
        if (!orgId)
            return [];
        const allItems = [];
        let nextToken;
        do {
            const result = await getGQLAPI().graphql({
                query: listInstructors,
                variables: {
                    organizationID: orgId,
                    limit: 100,
                    nextToken,
                },
                authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
            });
            const page = result?.data?.listInstructors;
            allItems.push(...(page?.items || []));
            nextToken = page?.nextToken ?? null;
        } while (nextToken);
        return allItems
            .filter((item) => String(item?.organizationID || '') === orgId)
            .sort((a, b) => {
            const authorityA = a?.aiVisibilityAuthority ? 0 : 1;
            const authorityB = b?.aiVisibilityAuthority ? 0 : 1;
            if (authorityA !== authorityB)
                return authorityA - authorityB;
            const orderA = Number.isFinite(Number(a?.sortOrder)) ? Number(a.sortOrder) : 0;
            const orderB = Number.isFinite(Number(b?.sortOrder)) ? Number(b.sortOrder) : 0;
            if (orderA !== orderB)
                return orderA - orderB;
            return String(a?.name || '').localeCompare(String(b?.name || ''));
        });
    };
    const updateInstructorAuthorityRecord = async (input) => {
        const result = await getGQLAPI().graphql({
            query: updateInstructorGql,
            variables: { input },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.updateInstructor) {
            throw Error('no data returned');
        }
        return result.data.updateInstructor;
    };
    const associateGuideProductsGql = /* GraphQL */ `mutation AssociateGuideProducts(
      $organizationID: ID!
      $guideId: ID!
      $mode: GuideProductAssociationMode!
      $handles: [String!]
      $limit: Int
      $previewOnly: Boolean
      $generationErrorRecovery: AWSJSON
    ) {
      associateGuideProducts(
        organizationID: $organizationID
        guideId: $guideId
        mode: $mode
        handles: $handles
        limit: $limit
        previewOnly: $previewOnly
        generationErrorRecovery: $generationErrorRecovery
      ) {
        statusCode
        guideId
        handles
        matchedCount
        mode
        message
        error
        generationErrorResolution
        updatedAt
        taskId
      }
    }`;
    const associateGuideProducts = async (variables) => {
        const result = await getGQLAPI().graphql({
            query: associateGuideProductsGql,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.associateGuideProducts) {
            throw Error('no data returned');
        }
        return result.data.associateGuideProducts;
    };
    const generateRelatedGuidesGql = /* GraphQL */ `mutation GenerateRelatedGuides(
      $organizationID: ID!
      $guideId: ID!
      $limit: Int
    ) {
      generateRelatedGuides(
        organizationID: $organizationID
        guideId: $guideId
        limit: $limit
      ) {
        statusCode
        guideId
        guideClusterID
        updatedGuideCount
        message
        error
        taskId
      }
    }`;
    const generateRelatedGuidesForClusterGql = /* GraphQL */ `mutation GenerateRelatedGuidesForCluster(
      $organizationID: ID!
      $guideClusterID: ID!
      $limitPerGuide: Int
    ) {
      generateRelatedGuidesForCluster(
        organizationID: $organizationID
        guideClusterID: $guideClusterID
        limitPerGuide: $limitPerGuide
      ) {
        statusCode
        guideClusterID
        updatedGuideCount
        message
        error
        taskId
      }
    }`;
    const embedRelatedGuideLinksGql = /* GraphQL */ `mutation EmbedRelatedGuideLinks(
      $organizationID: ID!
      $guideId: ID!
      $mode: String
    ) {
      embedRelatedGuideLinks(
        organizationID: $organizationID
        guideId: $guideId
        mode: $mode
      ) {
        statusCode
        guideId
        embeddedCount
        predictedCount
        message
        error
      }
    }`;
    const generateGuideImageGql = /* GraphQL */ `mutation GenerateGuideImage(
      $organizationID: ID!
      $guideId: ID!
      $prompt: String
      $references: AWSJSON
    ) {
      generateGuideImage(
        organizationID: $organizationID
        guideId: $guideId
        prompt: $prompt
        references: $references
      ) {
        statusCode
        guideId
        imageUrl
        assetId
        prompt
        message
        error
        taskId
      }
    }`;
    const listGuideImageReferenceProductsGql = /* GraphQL */ `mutation ListGuideImageReferenceProducts(
      $organizationID: ID!
      $query: String
      $handles: [String!]
      $limit: Int
      $nextToken: String
    ) {
      listGuideImageReferenceProducts(
        organizationID: $organizationID
        query: $query
        handles: $handles
        limit: $limit
        nextToken: $nextToken
      ) {
        statusCode
        items { handle title imageUrl }
        missingHandles
        nextToken
        message
        error
      }
    }`;
    const generateImageAltTextGql = /* GraphQL */ `mutation GenerateImageAltText(
      $organizationID: ID!
      $guideId: ID
      $imageUrl: String!
    ) {
      generateImageAltText(
        organizationID: $organizationID
        guideId: $guideId
        imageUrl: $imageUrl
      ) {
        statusCode
        altText
        message
        error
      }
    }`;
    const refineGuideImagePreviewGql = /* GraphQL */ `mutation RefineGuideImagePreview(
      $organizationID: ID!
      $guideId: ID!
      $refinementPrompt: String
      $previousImageUrl: String!
      $basePrompt: String
      $references: AWSJSON
    ) {
      refineGuideImagePreview(
        organizationID: $organizationID
        guideId: $guideId
        refinementPrompt: $refinementPrompt
        previousImageUrl: $previousImageUrl
        basePrompt: $basePrompt
        references: $references
      ) {
        statusCode
        guideId
        imageUrl
        assetId
        prompt
        message
        error
        taskId
      }
    }`;
    const generateRelatedGuides = async (variables) => {
        const result = await getGQLAPI().graphql({
            query: generateRelatedGuidesGql,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.generateRelatedGuides) {
            throw Error('no data returned');
        }
        return result.data.generateRelatedGuides;
    };
    const generateRelatedGuidesForCluster = async (variables) => {
        const result = await getGQLAPI().graphql({
            query: generateRelatedGuidesForClusterGql,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.generateRelatedGuidesForCluster) {
            throw Error('no data returned');
        }
        return result.data.generateRelatedGuidesForCluster;
    };
    const embedRelatedGuideLinks = async (variables) => {
        const result = await getGQLAPI().graphql({
            query: embedRelatedGuideLinksGql,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.embedRelatedGuideLinks) {
            throw Error('no data returned');
        }
        return result.data.embedRelatedGuideLinks;
    };
    const auditGuideClusterLinks = async (variables) => {
        const result = await getGQLAPI().graphql({
            query: auditGuideClusterLinksGql,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.auditGuideClusterLinks) {
            throw Error('no data returned');
        }
        return result.data.auditGuideClusterLinks;
    };
    const generateGuideImage = async (variables) => {
        const result = await getGQLAPI().graphql({
            query: generateGuideImageGql,
            variables: withGuideImageReferences({
                organizationID: variables.organizationID,
                guideId: variables.guideId,
                prompt: variables.prompt ?? null,
            }, variables.references),
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.generateGuideImage) {
            throw Error('no data returned');
        }
        return result.data.generateGuideImage;
    };
    const listGuideImageReferenceProducts = async (variables) => {
        const result = await getGQLAPI().graphql({
            query: listGuideImageReferenceProductsGql,
            variables: {
                organizationID: variables.organizationID,
                query: variables.query ?? null,
                handles: variables.handles ?? [],
                limit: variables.limit ?? 24,
                nextToken: variables.nextToken ?? null,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.listGuideImageReferenceProducts)
            throw Error('no data returned');
        return result.data.listGuideImageReferenceProducts;
    };
    const generateImageAltText = async (variables) => {
        const result = await getGQLAPI().graphql({
            query: generateImageAltTextGql,
            variables: {
                organizationID: variables.organizationID,
                guideId: variables.guideId ?? null,
                imageUrl: variables.imageUrl,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.generateImageAltText) {
            throw Error('no data returned');
        }
        return result.data.generateImageAltText;
    };
    const refineGuideImagePreview = async (variables) => {
        const result = await getGQLAPI().graphql({
            query: refineGuideImagePreviewGql,
            variables: withGuideImageReferences({
                organizationID: variables.organizationID,
                guideId: variables.guideId,
                refinementPrompt: variables.refinementPrompt ?? null,
                previousImageUrl: variables.previousImageUrl,
                basePrompt: variables.basePrompt ?? null,
            }, variables.references),
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.refineGuideImagePreview) {
            throw Error('no data returned');
        }
        return result.data.refineGuideImagePreview;
    };
    const runGuideGenerationAndPoll = async (variables) => {
        let baselineGuide = await getGuideRecord(variables.organizationID, variables.guideId);
        const processingResponse = (message = 'Processing') => ({
            statusCode: 102,
            guideId: variables.guideId,
            message,
        });
        const runGenerationStep = async () => (variables.operation === 'content'
            ? generateGuide({
                organizationID: variables.organizationID,
                guideId: variables.guideId,
                contentPromptOverride: variables.contentPromptOverride,
                additionalInstructions: variables.additionalInstructions,
                optimizationMode: variables.optimizationMode,
                bylineInstructorID: variables.bylineInstructorID ?? null,
                reviewerInstructorID: variables.reviewerInstructorID ?? null,
            })
            : regenerateGuideTopic({
                organizationID: variables.organizationID,
                guideId: variables.guideId,
                topicsPromptOverride: variables.topicsPromptOverride,
            }));
        const isDuplicateMessage = (message) => (String(message || '').toLowerCase().includes('already covers this topic'));
        const runAttempt = async () => {
            let resolverTimeoutReadOnlyUntil = 0;
            let latestPolledGuide = null;
            const pollGuideRecord = async () => {
                latestPolledGuide = await getGuideRecord(variables.organizationID, variables.guideId);
                const recordCompletion = getGuideGenerationCompletionFromRecord({
                    baselineGuide,
                    currentGuide: latestPolledGuide,
                    operation: variables.operation,
                });
                if (recordCompletion) {
                    return {
                        done: true,
                        value: recordCompletion,
                    };
                }
                return {
                    done: false,
                    value: processingResponse('Processing'),
                };
            };
            let kickoffResponse;
            try {
                kickoffResponse = await runGenerationStep();
            }
            catch (error) {
                if (!isLambdaExecutionTimeoutError(error)) {
                    throw error;
                }
                resolverTimeoutReadOnlyUntil = Date.now() + GUIDE_GENERATION_RESOLVER_TIMEOUT_READONLY_MS;
                kickoffResponse = processingResponse('Generation is still processing after the resolver timed out');
            }
            if (kickoffResponse?.statusCode !== 102) {
                return {
                    status: kickoffResponse?.statusCode === 200
                        ? (isDuplicateMessage(kickoffResponse?.message) ? 'duplicate' : 'completed')
                        : 'failed',
                    guide: kickoffResponse?.statusCode === 200
                        ? await getGuideRecord(variables.organizationID, variables.guideId)
                        : null,
                    existingGuideId: kickoffResponse?.existingGuideId ?? null,
                    message: kickoffResponse?.message ?? null,
                    error: kickoffResponse?.error ?? null,
                };
            }
            const pollResult = await pollWithBackoff({
                timeoutMs: GUIDE_GENERATION_POLL_TIMEOUT_MS,
                poll: async () => {
                    if (resolverTimeoutReadOnlyUntil > Date.now()) {
                        try {
                            return await pollGuideRecord();
                        }
                        catch {
                            return {
                                done: false,
                                value: processingResponse('Processing'),
                            };
                        }
                    }
                    let response;
                    try {
                        response = await runGenerationStep();
                    }
                    catch (error) {
                        if (!isLambdaExecutionTimeoutError(error)) {
                            throw error;
                        }
                        resolverTimeoutReadOnlyUntil = Date.now() + GUIDE_GENERATION_RESOLVER_TIMEOUT_READONLY_MS;
                        try {
                            return await pollGuideRecord();
                        }
                        catch {
                            return {
                                done: false,
                                value: processingResponse('Processing'),
                            };
                        }
                    }
                    if (response?.statusCode !== 102) {
                        latestPolledGuide = null;
                        return {
                            done: true,
                            value: response,
                        };
                    }
                    try {
                        latestPolledGuide = await getGuideRecord(variables.organizationID, variables.guideId);
                        const recordCompletion = getGuideGenerationCompletionFromRecord({
                            baselineGuide,
                            currentGuide: latestPolledGuide,
                            operation: variables.operation,
                        });
                        if (recordCompletion) {
                            return {
                                done: true,
                                value: recordCompletion,
                            };
                        }
                    }
                    catch {
                        /* Keep polling the Lambda response if the secondary record refresh fails. */
                    }
                    return {
                        done: false,
                        value: response,
                    };
                },
            });
            const finalResponse = pollResult.value;
            const finalGuide = latestPolledGuide ?? await getGuideRecord(variables.organizationID, variables.guideId);
            if (pollResult.status === 'timeout') {
                return {
                    status: 'timeout',
                    guide: finalGuide,
                    existingGuideId: null,
                    message: null,
                    error: null,
                };
            }
            if (finalResponse?.statusCode === 200) {
                const duplicateGuideDetected = isDuplicateMessage(finalResponse?.message)
                    || (variables.operation === 'topic'
                        && baselineGuide
                        && finalGuide
                        && baselineGuide.topic === finalGuide.topic
                        && baselineGuide.lastGeneratedAt !== finalGuide.lastGeneratedAt);
                return {
                    status: duplicateGuideDetected ? 'duplicate' : 'completed',
                    guide: finalGuide,
                    existingGuideId: finalResponse?.existingGuideId ?? null,
                    message: finalResponse?.message ?? null,
                    error: finalResponse?.error ?? null,
                };
            }
            return {
                status: 'failed',
                guide: finalGuide,
                existingGuideId: finalResponse?.existingGuideId ?? null,
                message: finalResponse?.message ?? null,
                error: finalResponse?.error ?? null,
            };
        };
        let retryUsed = false;
        while (true) {
            const result = await runAttempt();
            if (retryUsed
                || result.status !== 'failed'
                || !isTransientOpenAiGuideGenerationError(result, result.guide)) {
                return result;
            }
            retryUsed = true;
            baselineGuide = await getGuideRecord(variables.organizationID, variables.guideId);
        }
    };
    const runCoreGuideGenerationAndPoll = async (variables) => {
        const kickoffResponse = await generateCoreGuide(variables);
        if (kickoffResponse?.statusCode !== 102) {
            return {
                status: kickoffResponse?.statusCode === 200 ? 'completed' : 'failed',
                guide: kickoffResponse?.guideId
                    ? await getGuideRecord(variables.organizationID, kickoffResponse.guideId)
                    : null,
                guideId: kickoffResponse?.guideId ?? null,
                message: kickoffResponse?.message ?? null,
                error: kickoffResponse?.error ?? null,
            };
        }
        let latestGuide = null;
        const pollResult = await pollWithBackoff({
            timeoutMs: GUIDE_GENERATION_POLL_TIMEOUT_MS,
            poll: async () => {
                const response = await generateCoreGuide(variables);
                if (response?.guideId) {
                    try {
                        latestGuide = await getGuideRecord(variables.organizationID, response.guideId);
                    }
                    catch {
                        latestGuide = null;
                    }
                }
                // Only exit on a server-returned terminal status. The record-diff
                // secondary check has been removed — the server's 5-minute stale guard
                // ensures a terminal 500 arrives well before the 6-minute client timeout.
                if (response?.statusCode !== 102) {
                    return {
                        done: true,
                        value: response,
                    };
                }
                return {
                    done: false,
                    value: response,
                };
            },
        });
        const finalResponse = pollResult.value;
        const finalGuide = latestGuide || (finalResponse?.guideId
            ? await getGuideRecord(variables.organizationID, finalResponse.guideId)
            : null);
        if (pollResult.status === 'timeout') {
            return {
                status: 'timeout',
                guide: finalGuide,
                guideId: finalResponse?.guideId ?? kickoffResponse?.guideId ?? null,
                message: 'Still processing: Core Guide generation continues on the server after this client wait window. '
                    + 'Refresh clusters or open the guide in the editor to check status.',
                error: null,
            };
        }
        return {
            status: finalResponse?.statusCode === 200 ? 'completed' : 'failed',
            guide: finalGuide,
            guideId: finalResponse?.guideId ?? kickoffResponse?.guideId ?? null,
            message: finalResponse?.message ?? null,
            error: finalResponse?.error ?? null,
        };
    };
    // ── Image generation — ADR-0032 Task Manager (taskType 'generate_image') ──
    // The kickoff mutation returns a 102 + the durable AdminTask id; the central
    // manager (driveTasks → generate_image provider) drives the OpenAI image job
    // to completion. The client NO LONGER re-invokes the mutation to poll (that
    // path is the legacy browser stopgap). Instead it polls the AdminTask row
    // (getAdminTask) until terminal and reads the picker candidate straight off
    // the task's resultRef — the storage-decoupled Client API surface (§6).
    const getAdminTaskGql = /* GraphQL */ `query GetAdminTask($organizationID: ID!, $id: ID!) {
      getAdminTask(organizationID: $organizationID, id: $id) {
        id
        organizationID
        taskType
        status
        percent
        phase
        resultRef
        error
        createdAt
        updatedAt
        completedAt
      }
    }`;
    const getAdminTaskRecord = async (organizationID, id) => {
        const result = await getGQLAPI().graphql({
            query: getAdminTaskGql,
            variables: { organizationID, id },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        return result?.data?.getAdminTask ?? null;
    };
    // Terminal AdminTask statuses (mirrors the shared-layer state machine).
    const TERMINAL_TASK_STATUSES = new Set(['succeeded', 'failed', 'cancelled', 'timed_out']);
    // Parse the generate_image resultRef into the picker candidate shape the
    // GuideEditor/ImagePicker expect ({ imageUrl, assetId, prompt }). The
    // provider writes resultRef = { kind:'guide_image', url, assetId, altText,
    // prompt, source } (see guideImage.js buildGuideImageResult).
    // Use parseAwsJsonObject — AppSync AWSJSON may be double-encoded.
    const parseImageResultRef = (resultRef) => {
        const obj = parseAwsJsonObject(resultRef);
        if (!obj)
            return {};
        return {
            imageUrl: typeof obj.url === 'string' ? obj.url : undefined,
            assetId: typeof obj.assetId === 'string' ? obj.assetId : null,
            prompt: typeof obj.prompt === 'string' ? obj.prompt : null,
        };
    };
    // Map a kickoff response + a terminal/timed-out AdminTask row onto the
    // existing { status, response } contract the picker adapters consume.
    const imageTaskOutcome = (kickoff, pollResult) => {
        const task = pollResult.value;
        if (pollResult.status === 'timeout' || !task) {
            return {
                status: 'timeout',
                response: {
                    statusCode: 102,
                    taskId: kickoff.taskId ?? null,
                    guideId: kickoff.guideId ?? null,
                    message: 'Still running — it will appear in Tasks when ready.',
                },
            };
        }
        if (task.status === 'succeeded') {
            const candidate = parseImageResultRef(task.resultRef);
            return {
                status: 'completed',
                response: {
                    statusCode: 200,
                    taskId: task.id,
                    guideId: kickoff.guideId ?? null,
                    imageUrl: candidate.imageUrl,
                    assetId: candidate.assetId ?? null,
                    prompt: candidate.prompt ?? null,
                },
            };
        }
        // failed / cancelled / timed_out
        return {
            status: 'failed',
            response: {
                statusCode: 500,
                taskId: task.id,
                guideId: kickoff.guideId ?? null,
                error: task.error || 'Image operation failed.',
            },
        };
    };
    const pollImageAdminTask = async (organizationID, taskId) => pollWithBackoff({
        timeoutMs: GUIDE_IMAGE_POLL_TIMEOUT_MS,
        poll: async () => {
            const task = await getAdminTaskRecord(organizationID, taskId);
            return {
                done: Boolean(task && TERMINAL_TASK_STATUSES.has(task.status)),
                value: task,
            };
        },
    });
    const runGuideImageGenerateAndPoll = async (variables) => {
        const kickoffResponse = await generateGuideImage({
            organizationID: variables.organizationID,
            guideId: variables.guideId,
            prompt: variables.prompt ?? null,
            references: variables.references,
        });
        // Non-102 kickoff: synchronous terminal result (e.g. validation 4xx, or a
        // legacy 200). Pass it straight through.
        if (kickoffResponse?.statusCode !== 102) {
            return {
                status: kickoffResponse?.statusCode === 200 ? 'completed' : 'failed',
                response: kickoffResponse,
            };
        }
        // No durable task id (createTask failed server-side) — we cannot poll the
        // ledger, so surface a timeout the picker treats as "running in Tasks".
        if (!kickoffResponse?.taskId) {
            return {
                status: 'timeout',
                response: kickoffResponse,
            };
        }
        const pollResult = await pollImageAdminTask(variables.organizationID, kickoffResponse.taskId);
        return imageTaskOutcome({ taskId: kickoffResponse.taskId, guideId: variables.guideId }, pollResult);
    };
    const runGuideImageRefineAndPoll = async (variables) => {
        const kickoffResponse = await refineGuideImagePreview({
            organizationID: variables.organizationID,
            guideId: variables.guideId,
            previousImageUrl: variables.previousImageUrl,
            refinementPrompt: variables.refinementPrompt ?? null,
            basePrompt: variables.basePrompt ?? null,
            references: variables.references,
        });
        if (kickoffResponse?.statusCode !== 102) {
            return {
                status: kickoffResponse?.statusCode === 200 ? 'completed' : 'failed',
                response: kickoffResponse,
            };
        }
        if (!kickoffResponse?.taskId) {
            return {
                status: 'timeout',
                response: kickoffResponse,
            };
        }
        const pollResult = await pollImageAdminTask(variables.organizationID, kickoffResponse.taskId);
        return imageTaskOutcome({ taskId: kickoffResponse.taskId, guideId: variables.guideId }, pollResult);
    };
    const runGuideDiscoveryAndPoll = async (variables) => {
        const baselineGuides = sortGuidesForAdmin(await listGuidesForAdmin(variables.organizationID, 100));
        const kickoffPromise = generateGuidesFromUrl(variables);
        const earlyKickoffSettlement = await settlePromiseWithin(kickoffPromise, 800);
        if (earlyKickoffSettlement.status === 'rejected') {
            if (!isExpectedLongRunningGraphqlError(earlyKickoffSettlement.error)) {
                throw earlyKickoffSettlement.error;
            }
        }
        else if (earlyKickoffSettlement.status === 'resolved') {
            return {
                status: 'completed',
                guides: sortGuidesForAdmin(await listGuidesForAdmin(variables.organizationID, 100)),
                message: earlyKickoffSettlement.value?.message ?? 'Guide opportunities processed.',
            };
        }
        const pollResult = await pollWithBackoff({
            poll: async () => {
                const guides = sortGuidesForAdmin(await listGuidesForAdmin(variables.organizationID, 100));
                const delta = getGuideListDelta(baselineGuides, guides, variables.url);
                return {
                    done: delta.length > 0,
                    value: guides,
                };
            },
        });
        if (pollResult.status === 'completed') {
            return {
                status: 'completed',
                guides: pollResult.value,
                message: 'Guide opportunities updated.',
            };
        }
        const kickoffSettlement = await settlePromiseWithin(kickoffPromise, 1200);
        if (kickoffSettlement.status === 'rejected') {
            if (!isExpectedLongRunningGraphqlError(kickoffSettlement.error)) {
                throw kickoffSettlement.error;
            }
            return {
                status: 'timeout',
                guides: sortGuidesForAdmin(await listGuidesForAdmin(variables.organizationID, 100)),
                message: 'Guide discovery may still be running. Refresh again in a moment if no new guides appear yet.',
            };
        }
        if (kickoffSettlement.status === 'resolved') {
            return {
                status: 'completed',
                guides: sortGuidesForAdmin(await listGuidesForAdmin(variables.organizationID, 100)),
                message: kickoffSettlement.value?.message ?? 'Guide opportunities processed.',
            };
        }
        return {
            status: 'timeout',
            guides: sortGuidesForAdmin(await listGuidesForAdmin(variables.organizationID, 100)),
            message: 'Guide discovery may still be running. Refresh again in a moment if no new guides appear yet.',
        };
    };
    const setEnvironmentConfig = (newValue) => {
        self.environmentConfig = newValue;
    };
    const getEnvironmentConfig = async () => {
        const result = await getGQLAPI().graphql({
            query: getEnvironmentConfigGQL,
            variables: {},
            authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
        });
        if (!result?.data?.getConfig) {
            throw Error('no data returned');
        }
        self.setEnvironmentConfig(result.data.getConfig);
        return self.environmentConfig;
    };
    const startCrawl = async (crawl) => {
        if (!self.currentOrganization) {
            throw Error('currentOrganazation cannot be empty');
        }
        console.log('startCrawl called');
        const response = await gqlCall(startCrawlGQL, {
            organizationID: self.currentOrganization.id,
            crawlID: crawl.id,
        });
        const result = response.data.startCrawl;
        const organization = self.remoteItemToLocal('organization', result.organization);
        self.addItemToPoll('organization', organization.id, organization.id, 'not used', organization.updatedAt, (updatedOrganization) => {
            console.log('Ok, Im updated', updatedOrganization);
            // here check status of crawl and remove from polling list if complete
            const newCrawl = JSON.parse(updatedOrganization.crawlConfigs).find((c) => c.id === crawl.id);
            console.log('newCrawl', newCrawl);
            const existingCrawl = organization.getCrawlConfig(crawl.id);
            existingCrawl.updateValues({
                status: newCrawl.status,
                pageCount: newCrawl.pageCount,
                processedCount: newCrawl.processedCount,
            });
            if (newCrawl.status === 'completed') {
                self.removeItemFromPoll('organization', organization.id);
                self.remoteItemToLocal('organization', updatedOrganization);
                navStore.showSnackBar(`Crawl complete: ${newCrawl.name}`);
            }
        });
        return {
            ...result,
            organization,
        };
    };
    const getCrawlStatus = async (crawlID) => {
        if (!self.currentOrganization) {
            throw Error('currentOrganazation cannot be empty');
        }
        console.log('getCrawlStatus called');
        const response = await gqlCall(getCrawlStatusGQL, {
            organizationID: self.currentOrganization.id,
            crawlID,
        });
        const result = response.data.getCrawlStatus;
        const organization = self.remoteItemToLocal('organization', result.organization);
        return {
            ...result,
            organization,
        };
    };
    const getCrawledUrls = async (crawlID) => {
        if (!self.currentOrganization) {
            throw Error('currentOrganazation cannot be empty');
        }
        console.log('getCrawlStatus called');
        const response = await gqlCall(getCrawledUrlsGQL, {
            organizationID: self.currentOrganization.id,
            crawlID,
        });
        const result = response.data.getCrawledUrls;
        return result.message;
    };
    const getAiDashboard = async (portalKey, startDate, endDate) => {
        const result = await getGQLAPI().graphql({
            query: getAiDashboardGQL,
            variables: { portalKey, startDate, endDate },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.getAiDashboard) {
            throw Error('no data returned');
        }
        return result.data.getAiDashboard;
    };
    const parseAppSyncAwsJson = (raw) => {
        if (raw == null) {
            return null;
        }
        if (typeof raw === 'string') {
            return JSON.parse(raw);
        }
        return raw;
    };
    const getGeoReporting = async (organizationID, startDate, endDate, timezone) => {
        const result = await getGQLAPI().graphql({
            query: getGeoReportingGQL,
            variables: {
                organizationID,
                startDate,
                endDate,
                timezone: timezone ?? null,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        const raw = result?.data?.getGeoReporting;
        if (raw == null) {
            throw Error('no data returned');
        }
        return parseAppSyncAwsJson(raw);
    };
    const getGeoGuideDrilldown = async (organizationID, guideId, startDate, endDate) => {
        const result = await getGQLAPI().graphql({
            query: getGeoGuideDrilldownGQL,
            variables: {
                organizationID,
                guideId,
                startDate,
                endDate,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        const raw = result?.data?.getGeoGuideDrilldown;
        if (raw == null) {
            throw Error('no data returned');
        }
        return parseAppSyncAwsJson(raw);
    };
    const getPageViewStats = async (organizationID, startDate, endDate, filters) => {
        const result = await getGQLAPI().graphql({
            query: getPageViewStatsGQL,
            variables: {
                organizationID,
                startDate,
                endDate,
                filters: filters ? JSON.stringify(filters) : null,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.getPageViewStats) {
            throw Error('no data returned');
        }
        return result.data.getPageViewStats;
    };
    const getTopProducts = async (organizationID, startDate, endDate) => {
        const result = await getGQLAPI().graphql({
            query: getTopProductsGQL,
            variables: {
                organizationID,
                startDate,
                endDate,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.getTopProducts) {
            throw Error('no data returned');
        }
        return result.data.getTopProducts;
    };
    const getConversationSummary = async (organizationID, startDate, endDate, timezoneOffset) => {
        const result = await getGQLAPI().graphql({
            query: getConversationSummaryGQL,
            variables: {
                organizationID,
                startDate,
                endDate,
                timezoneOffset,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.getConversationSummary) {
            throw Error('no data returned');
        }
        return result.data.getConversationSummary;
    };
    const getTopicsHierarchy = async (organizationID) => {
        console.log('[AdminStore.getTopicsHierarchy] Calling GraphQL with organizationID:', organizationID);
        const result = await getGQLAPI().graphql({
            query: getTopicsHierarchyGQL,
            variables: {
                organizationID,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        console.log('[AdminStore.getTopicsHierarchy] GraphQL result:', result);
        console.log('[AdminStore.getTopicsHierarchy] Result data:', result?.data);
        console.log('[AdminStore.getTopicsHierarchy] getTopicsHierarchy data:', result?.data?.getTopicsHierarchy);
        if (!result?.data?.getTopicsHierarchy) {
            console.error('[AdminStore.getTopicsHierarchy] No data returned!');
            throw Error('no data returned');
        }
        return result.data.getTopicsHierarchy;
    };
    const saveTopicsHierarchy = async (organizationID, topics) => {
        const result = await getGQLAPI().graphql({
            query: saveTopicsHierarchyGQL,
            variables: {
                organizationID,
                topics: JSON.stringify(topics),
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.saveTopicsHierarchy) {
            throw Error('no data returned');
        }
        return result.data.saveTopicsHierarchy;
    };
    const getSessionDetail = async (organizationID, sessionID, startDate, endDate) => {
        const result = await getGQLAPI().graphql({
            query: getSessionDetailGQL,
            variables: {
                organizationID,
                sessionID,
                startDate: startDate || null,
                endDate: endDate || null,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.getSessionDetail) {
            throw Error('no data returned');
        }
        return result.data.getSessionDetail;
    };
    const getSessionsList = async (organizationID, startDate, endDate, limit, offset, filters) => {
        const variables = {
            organizationID,
            startDate,
            endDate,
        };
        if (limit !== undefined && limit !== null) {
            variables.limit = limit;
        }
        // Only include offset if it's defined (don't default to 0)
        if (offset !== undefined && offset !== null) {
            variables.offset = offset;
        }
        if (filters) {
            variables.filters = JSON.stringify(filters);
        }
        const result = await getGQLAPI().graphql({
            query: getSessionsListGQL,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.getSessionsList) {
            throw Error('no data returned');
        }
        return result.data.getSessionsList;
    };
    const getConversationsList = async (organizationID, startDate, endDate, limit, offset, filters) => {
        // Log parameters to debug parameter passing
        console.log('getConversationsList called with params:', {
            organizationID,
            startDate,
            endDate,
            limit,
            offset,
            filters,
            offsetType: typeof offset,
            offsetIsUndefined: offset === undefined,
            offsetIsNull: offset === null,
        });
        const variables = {
            organizationID,
            startDate,
            endDate,
        };
        if (limit !== undefined && limit !== null) {
            variables.limit = limit;
        }
        // Only include offset if it's defined (don't default to 0)
        if (offset !== undefined && offset !== null) {
            variables.offset = offset;
        }
        if (filters) {
            variables.filters = JSON.stringify(filters);
        }
        console.log('getConversationsList variables:', JSON.stringify(variables, null, 2));
        const result = await getGQLAPI().graphql({
            query: getConversationsListGQL,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.getConversationsList) {
            throw Error('no data returned');
        }
        return result.data.getConversationsList;
    };
    const classifyConversation = async (organizationID, conversationID) => {
        const classifyConversationGql = /* GraphQL */ `mutation ClassifyConversation($organizationID: String!, $conversationID: String!) {
        classifyConversation(
          organizationID: $organizationID
          conversationID: $conversationID
        ) {
          statusCode
          conversationID
          organizationID
          classification
          conversationData
        }
      }`;
        const result = await getGQLAPI().graphql({
            query: classifyConversationGql,
            variables: {
                organizationID,
                conversationID,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.classifyConversation) {
            throw Error('no data returned from classifyConversation');
        }
        const { classification: rawClassification, conversationData: rawConversationData, } = result.data.classifyConversation;
        // Parse classification and conversationData if they're JSON strings
        let classification = null;
        if (rawClassification) {
            if (typeof rawClassification === 'string') {
                classification = JSON.parse(rawClassification);
            }
            else {
                classification = rawClassification;
            }
        }
        let conversationData = null;
        if (rawConversationData) {
            if (typeof rawConversationData === 'string') {
                conversationData = JSON.parse(rawConversationData);
            }
            else {
                conversationData = rawConversationData;
            }
        }
        return {
            classification,
            conversationData,
        };
    };
    const getConversationDetail = async (organizationID, conversationID, startDate, endDate) => {
        const result = await getGQLAPI().graphql({
            query: getConversationDetailGQL,
            variables: {
                organizationID,
                conversationID,
                startDate: startDate || null,
                endDate: endDate || null,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.getConversationDetail) {
            throw Error('no data returned');
        }
        return result.data.getConversationDetail;
    };
    const getEventsForSession = async (organizationID, sessionID, startDate, endDate) => {
        const result = await getGQLAPI().graphql({
            query: getEventsForSessionGQL,
            variables: {
                organizationID,
                sessionID,
                startDate: startDate || null,
                endDate: endDate || null,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.getEventsForSession) {
            throw Error('no data returned');
        }
        return result.data.getEventsForSession;
    };
    const getPageViewsForUrl = async (organizationID, startDate, endDate, urlMatch, filters) => {
        const result = await getGQLAPI().graphql({
            query: getPageViewsForUrlGQL,
            variables: {
                organizationID,
                startDate,
                endDate,
                urlMatch,
                filters: filters ? JSON.stringify(filters) : null,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.getPageViewsForUrl) {
            throw Error('no data returned');
        }
        return result.data.getPageViewsForUrl;
    };
    const getSessionsGeography = async (organizationID, startDate, endDate, filters) => {
        const result = await getGQLAPI().graphql({
            query: getSessionsGeographyGQL,
            variables: {
                organizationID,
                startDate,
                endDate,
                filters: filters ? JSON.stringify(filters) : null,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.getSessionsGeography) {
            throw Error('no data returned');
        }
        return result.data.getSessionsGeography;
    };
    const getPageViewsGeography = async (organizationID, startDate, endDate, filters) => {
        const result = await getGQLAPI().graphql({
            query: getPageViewsGeographyGQL,
            variables: {
                organizationID,
                startDate,
                endDate,
                filters: filters ? JSON.stringify(filters) : null,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.getPageViewsGeography) {
            throw Error('no data returned');
        }
        return result.data.getPageViewsGeography;
    };
    const runProductFeed = async (organizationID, fullLoad = false, syncConfigID) => {
        const runProductFeedGql = syncConfigID
            ? /* GraphQL */ `mutation RunProductFeed($organizationID: String!, $fullLoad: Boolean, $syncConfigID: String!) {
        runProductFeed(
          organizationID: $organizationID
          fullLoad: $fullLoad
          syncConfigID: $syncConfigID
        )
      }`
            : /* GraphQL */ `mutation RunProductFeed($organizationID: String!, $fullLoad: Boolean) {
        runProductFeed(
          organizationID: $organizationID
          fullLoad: $fullLoad
        )
      }`;
        const result = await getGQLAPI().graphql({
            query: runProductFeedGql,
            variables: syncConfigID
                ? { organizationID, fullLoad, syncConfigID }
                : { organizationID, fullLoad },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.runProductFeed) {
            throw Error('no data returned from runProductFeed');
        }
        return result.data.runProductFeed;
    };
    const refreshSearchGuidance = async (organizationID) => {
        const refreshSearchGuidanceGql = /* GraphQL */ `mutation RefreshSearchGuidance($organizationID: String!) {
        refreshSearchGuidance(organizationID: $organizationID)
      }`;
        const result = await getGQLAPI().graphql({
            query: refreshSearchGuidanceGql,
            variables: { organizationID },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.refreshSearchGuidance) {
            throw Error('no data returned from refreshSearchGuidance');
        }
        return result.data.refreshSearchGuidance;
    };
    const generateProductSheet = async (organizationID, productID) => {
        const generateProductSheetGql = /* GraphQL */ `mutation GenerateProductSheet($organizationID: String!, $productID: String!) {
        generateProductSheet(
          organizationID: $organizationID
          productID: $productID
        )
      }`;
        const result = await getGQLAPI().graphql({
            query: generateProductSheetGql,
            variables: { organizationID, productID },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (result?.errors?.length) {
            throw new Error(result.errors.map((e) => e.message).join('; '));
        }
        return result?.data?.generateProductSheet ?? 'OK';
    };
    /**
     * Re-sync mutation: { status, message?, product? }
     * Same product fields as getProduct when successful.
     */
    const reSyncProduct = async (organizationID, productID) => {
        const productFields = Schema.getGqlPropertyList('product', self.isPortalApi).join(' ');
        const reSyncProductGql = /* GraphQL */ `mutation ReSyncProduct($organizationID: String!, $productID: String!) {
        reSyncProduct(
          organizationID: $organizationID
          productID: $productID
        ) {
          status
          message
          product {
            ${productFields}
          }
        }
      }`;
        const result = await getGQLAPI().graphql({
            query: reSyncProductGql,
            variables: { organizationID, productID },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (result?.errors?.length) {
            throw new Error(result.errors.map((e) => e.message).join('; '));
        }
        const reSyncRes = (result?.data?.reSyncProduct ?? {
            status: 'ERROR',
            message: 'No response',
            product: null,
        });
        const productKey = resolveKey(String(productID), organizationID, 'product');
        // Merge updated Product into the MST store here (store layer), not in UI.
        if (reSyncRes?.status === 'SUCCESS' && reSyncRes?.product) {
            const localItem = self.getMapOf('product').get(productKey);
            if (localItem) {
                Schema.mapValuesFromGet('product', reSyncRes.product, localItem);
                if (typeof localItem.resetLoadedAt === 'function') {
                    localItem.resetLoadedAt();
                }
            }
            else {
                // If the product isn't already loaded in the current view, force a refresh so UI updates.
                await self.load('product', productKey, -1);
            }
        }
        else {
            // Ensure UI reflects correct syncState for NOT_FOUND/ERROR by forcing a refresh.
            try {
                await self.load('product', productKey, -1);
            }
            catch {
                // load() should update syncState; swallow errors to keep mutation result usable.
            }
        }
        return reSyncRes;
    };
    const productPageContent = async (organizationID, productID, portalID, locale) => {
        const productPageContentGql = /* GraphQL */ `query ProductPageContent(
        $organizationID: String!,
        $productID: String!,
        $portalID: String,
        $locale: String
      ) {
        productPageContent(
          organizationID: $organizationID
          productID: $productID
          portalID: $portalID
          locale: $locale
        ) {
          status
          message
          createdAt
          systemPrompt
          modelID
          suggestedPrompts
          smartSummary {
            header
            bullets {
              title
              body
            }
            generatedByAiLabel
          }
          seoContent {
            metaDescription
            geoSummary
          }
        }
      }`;
        const result = await getGQLAPI().graphql({
            query: productPageContentGql,
            variables: {
                organizationID,
                productID,
                portalID: portalID ?? null,
                locale: locale ?? null,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (result?.errors?.length) {
            throw new Error(result.errors.map((e) => e.message).join('; '));
        }
        return result?.data?.productPageContent ?? null;
    };
    const generateProductPageContent = async (organizationID, productID, portalID, locale, modelOverride) => {
        const generateProductPageContentGql = /* GraphQL */ `mutation GenerateProductPageContent(
        $organizationID: String!,
        $productID: String!,
        $portalID: String,
        $locale: String,
        $modelOverride: String
      ) {
        generateProductPageContent(
          organizationID: $organizationID
          productID: $productID
          portalID: $portalID
          locale: $locale
          modelOverride: $modelOverride
        ) {
          status
          message
          createdAt
          systemPrompt
          modelID
          suggestedPrompts
          smartSummary {
            header
            bullets {
              title
              body
            }
            generatedByAiLabel
          }
          seoContent {
            metaDescription
            geoSummary
          }
        }
      }`;
        const result = await getGQLAPI().graphql({
            query: generateProductPageContentGql,
            variables: {
                organizationID,
                productID,
                portalID: portalID ?? null,
                locale: locale ?? null,
                modelOverride: modelOverride != null && String(modelOverride).trim() !== ''
                    ? String(modelOverride).trim()
                    : null,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (result?.errors?.length) {
            throw new Error(result.errors.map((e) => e.message).join('; '));
        }
        return result?.data?.generateProductPageContent ?? null;
    };
    const portalSuggestedPromptAnswers = async (organizationID, portalID, locale) => {
        const portalSuggestedPromptAnswersGql = /* GraphQL */ `query PortalSuggestedPromptAnswers(
        $organizationID: String!,
        $portalID: String!,
        $locale: String
      ) {
        portalSuggestedPromptAnswers(
          organizationID: $organizationID
          portalID: $portalID
          locale: $locale
        ) {
          status
          message
          locales
          items {
            prompt
            promptType
            locale
            answer
            cacheStatus
            cachedAt
          }
        }
      }`;
        const result = await getGQLAPI().graphql({
            query: portalSuggestedPromptAnswersGql,
            variables: {
                organizationID,
                portalID,
                locale: locale ?? null,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (result?.errors?.length) {
            throw new Error(result.errors.map((e) => e.message).join('; '));
        }
        return result?.data?.portalSuggestedPromptAnswers ?? null;
    };
    const regeneratePortalSuggestedPromptAnswer = async (organizationID, portalID, promptType, prompt, locale) => {
        const regeneratePortalSuggestedPromptAnswerGql = /* GraphQL */ `mutation RegeneratePortalSuggestedPromptAnswer(
        $organizationID: String!,
        $portalID: String!,
        $promptType: String!,
        $prompt: String!,
        $locale: String
      ) {
        regeneratePortalSuggestedPromptAnswer(
          organizationID: $organizationID
          portalID: $portalID
          promptType: $promptType
          prompt: $prompt
          locale: $locale
        ) {
          status
          message
          prompt
          promptType
          answer
          cachedAt
        }
      }`;
        const result = await getGQLAPI().graphql({
            query: regeneratePortalSuggestedPromptAnswerGql,
            variables: {
                organizationID,
                portalID,
                promptType,
                prompt,
                locale: locale ?? null,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (result?.errors?.length) {
            throw new Error(result.errors.map((e) => e.message).join('; '));
        }
        return result?.data?.regeneratePortalSuggestedPromptAnswer ?? null;
    };
    const checkProductFeedStatus = async (organizationID, feedType, id) => {
        const checkProductFeedStatusGql = /* GraphQL */ `query CheckProductFeedStatus($organizationID: String!, $feedType: String!, $id: String!) {
        checkProductFeedStatus(
          organizationID: $organizationID
          feedType: $feedType
          id: $id
        ) {
          productFeed {
            lastStarted
            lastCompleted
            status
            message
            products
          }
          currentStatus
        }
      }`;
        const result = await getGQLAPI().graphql({
            query: checkProductFeedStatusGql,
            variables: {
                organizationID,
                feedType,
                id,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.checkProductFeedStatus) {
            throw Error('no data returned from checkProductFeedStatus');
        }
        return result.data.checkProductFeedStatus;
    };
    const enableWebPixel = async (organizationID, id) => {
        const enableWebPixelGql = /* GraphQL */ `mutation EnableWebPixel($organizationID: String!, $shopifyConfigId: String!) {
        enableWebPixel(
          organizationID: $organizationID
          shopifyConfigId: $shopifyConfigId
        ) {
          success
          message
        }
      }`;
        const result = await getGQLAPI().graphql({
            query: enableWebPixelGql,
            variables: {
                organizationID,
                shopifyConfigId: id,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.enableWebPixel) {
            throw Error('no data returned from enableWebPixel');
        }
        return result.data.enableWebPixel;
    };
    const disableWebPixel = async (organizationID, id) => {
        const disableWebPixelGql = /* GraphQL */ `mutation DisableWebPixel($organizationID: String!, $shopifyConfigId: String!) {
        disableWebPixel(
          organizationID: $organizationID
          shopifyConfigId: $shopifyConfigId
        ) {
          success
          message
        }
      }`;
        const result = await getGQLAPI().graphql({
            query: disableWebPixelGql,
            variables: {
                organizationID,
                shopifyConfigId: id,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.disableWebPixel) {
            throw Error('no data returned from disableWebPixel');
        }
        return result.data.disableWebPixel;
    };
    const getWebPixelStatus = async (organizationID, id) => {
        const getWebPixelStatusGql = /* GraphQL */ `query GetWebPixelStatus($organizationID: String!, $shopifyConfigId: String!) {
        getWebPixelStatus(
          organizationID: $organizationID
          shopifyConfigId: $shopifyConfigId
        ) {
          exists
          id
          settings {
            organizationID
            beaconURL
          }
        }
      }`;
        const result = await getGQLAPI().graphql({
            query: getWebPixelStatusGql,
            variables: {
                organizationID,
                shopifyConfigId: id,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.getWebPixelStatus) {
            throw Error('no data returned from getWebPixelStatus');
        }
        return result.data.getWebPixelStatus;
    };
    const getShopifySubscriptionStatus = async (organizationID, id) => {
        const getShopifySubscriptionStatusGql = /* GraphQL */ `query GetShopifySubscriptionStatus($organizationID: String!, $shopifyConfigId: String!) {
        getShopifySubscriptionStatus(
          organizationID: $organizationID
          shopifyConfigId: $shopifyConfigId
        ) {
          subscriptions {
            id
            name
            status
            createdAt
            currentPeriodEnd
            lineItems {
              id
              plan {
                pricingDetails {
                  price {
                    amount
                    currencyCode
                  }
                  interval
                }
              }
            }
          }
          shop {
            name
            id
          }
        }
      }`;
        const result = await getGQLAPI().graphql({
            query: getShopifySubscriptionStatusGql,
            variables: {
                organizationID,
                shopifyConfigId: id,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.getShopifySubscriptionStatus) {
            throw Error('no data returned from getShopifySubscriptionStatus');
        }
        return result.data.getShopifySubscriptionStatus;
    };
    const getShopifyGrantedScopes = async (organizationID, id) => {
        const getShopifyGrantedScopesGql = /* GraphQL */ `query GetShopifyGrantedScopes($organizationID: String!, $shopifyConfigId: String!) {
        getShopifyGrantedScopes(
          organizationID: $organizationID
          shopifyConfigId: $shopifyConfigId
        ) {
          accessScopes {
            handle
          }
        }
      }`;
        const result = await getGQLAPI().graphql({
            query: getShopifyGrantedScopesGql,
            variables: {
                organizationID,
                shopifyConfigId: id,
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result?.data?.getShopifyGrantedScopes) {
            throw Error('no data returned from getShopifyGrantedScopes');
        }
        return result.data.getShopifyGrantedScopes;
    };
    return {
        assistantStartThread,
        assistantGetMessages,
        assistantPostMessage,
        assistantRunThread,
        assistantGetRun,
        assistantStartGenerateField,
        assistantGetGenerateFieldResponse,
        assistantGetCourseTranscripts,
        generateField,
        getGuideRecord,
        listGuidesForAdmin,
        listGuideClustersForAdmin,
        getGuideClusterRecord,
        createGuideClusterRecord,
        updateGuideClusterRecord,
        deleteGuideClusterRecord,
        listGuideDiscoveryProposalRowsForJob,
        updateGuideDiscoveryProposalRowRecord,
        createClustersAndStubGuidesFromRows,
        createGuideRecord,
        updateGuideRecord,
        deleteGuideRecord,
        deleteGuideCascade,
        rebuildGuideBlogIndex,
        generateGuide,
        scoreGuide,
        startSuggestGuideCitationDomains,
        continueSuggestGuideCitationDomains,
        suggestGuideCitationDomains,
        auditShopifySitemap,
        generateShopifyGeoSitemap,
        applyShopifyGeoSitemapAsset,
        auditShopifyRobots,
        applyShopifyRobotsTemplate,
        generateCoreGuide,
        runCoreGuideGenerationAndPoll,
        regenerateGuideTopic,
        generateGuidesFromUrl,
        generateLlmsTxt,
        startGuideDiscoveryPlan,
        resumeGuideDiscoveryPlan,
        planGuideDiscoveryFromUrl,
        publishGuide,
        unpublishGuide,
        deleteGuideClusterCascade,
        publishInstructorBio,
        listGuideShopifyArticleTemplates,
        listActiveShopifyConfigsForOrganization,
        listAiVisibilityInstructorsForOrganization,
        updateInstructorAuthorityRecord,
        associateGuideProducts,
        generateRelatedGuides,
        generateRelatedGuidesForCluster,
        embedRelatedGuideLinks,
        auditGuideClusterLinks,
        generateGuideImage,
        listGuideImageReferenceProducts,
        refineGuideImagePreview,
        generateImageAltText,
        runGuideGenerationAndPoll,
        runGuideImageGenerateAndPoll,
        runGuideImageRefineAndPoll,
        runGuideDiscoveryAndPoll,
        getAiDashboard,
        getGeoReporting,
        getGeoGuideDrilldown,
        getPageViewStats,
        getTopProducts,
        getConversationSummary,
        getTopicsHierarchy,
        saveTopicsHierarchy,
        getSessionDetail,
        getSessionsList,
        getConversationsList,
        classifyConversation,
        getConversationDetail,
        getEventsForSession,
        getSessionsGeography,
        getPageViewsGeography,
        getPageViewsForUrl,
        getEnvironmentConfig,
        setEnvironmentConfig,
        addItemToSave,
        deployOrganization,
        deployPortal,
        deployRepository,
        getItemsToSave,
        saveAll,
        sendTestEmail,
        runProductFeed,
        refreshSearchGuidance,
        generateProductSheet,
        reSyncProduct,
        productPageContent,
        generateProductPageContent,
        portalSuggestedPromptAnswers,
        regeneratePortalSuggestedPromptAnswer,
        checkProductFeedStatus,
        enableWebPixel,
        disableWebPixel,
        getWebPixelStatus,
        getShopifySubscriptionStatus,
        getShopifyGrantedScopes,
        setAssistantSendDisabled(newValue) {
            self.assistantSendDisabled = newValue;
        },
        setReportDateRange(startDate, endDate) {
            self.reportStartDate = startDate;
            self.reportEndDate = endDate;
        },
        getReportStartDate() {
            return self.reportStartDate;
        },
        getReportEndDate() {
            return self.reportEndDate;
        },
        setReportTimezone(timezone) {
            self.reportTimezone = timezone;
        },
        getReportTimezone() {
            return self.reportTimezone;
        },
        getInWysiwygEditorSelect() { return inWysiwygEditorSelect; },
        setInWysiwygEditorSelect(newValue) {
            console.log('inWysiwygEditorSelect Value', newValue);
            inWysiwygEditorSelect = newValue;
        },
        startCrawl,
        getCrawlStatus,
        getCrawledUrls,
    };
});
export const AdminStore = types.compose(S3FileModel, MainModel, AdminModel).named('AdminStore');
const defaultSnapshot = {
    status: 'not_loaded',
    amplifyEnvironment: '',
    isAnonymous: false,
    repositories: {},
    categories: {},
    programs: {},
    courses: {},
    state: 'done',
    fileState: 'done',
};
const initialSnapshot = defaultSnapshot;
export const adminStore = AdminStore.create(initialSnapshot);
setResolver(adminStore); // hacky way to break circular dependency
const setAuthToken = async () => {
    await Auth.currentAuthenticatedUser()
        // .then((user:any) => {
        // const token = user.signInUserSession.idToken.jwtToken;
        // console.log(`Setting token on client: ${token}`);
        // })
        .then(() => (Auth.currentUserCredentials()))
        .then((credentials) => {
        console.log('identityId', credentials.identityId);
        adminStore.setIdentityId(credentials.identityId);
        adminStore.setStatus('not_loaded');
        console.log('Credentials', credentials);
        return credentials;
    })
        .catch((reject) => {
        console.log('user not authenticated', reject);
        adminStore.setStatus('not_authenticated');
    });
};
const refreshAuthToken = async () => {
    await Auth.currentAuthenticatedUser()
        .then(() => (Auth.currentUserCredentials()))
        .then((credentials) => {
        // console.log('identityId', credentials.identityId);
        adminStore.setIdentityId(credentials.identityId);
        return credentials;
    })
        .catch((reject) => {
        console.log('user not authenticated', reject);
        adminStore.setStatus('not_authenticated');
    });
};
const isUsableOrganization = (org) => {
    if (!org?.id)
        return false;
    if (org.deletedAt)
        return false;
    if (org.status === 'DELETED' || org.status === 'PURGING' || org.status === 'PURGED') {
        return false;
    }
    return true;
};
/** Hydrate organization map from userInfo and any missing org IDs on user-org links. */
const loadAccessibleOrganizations = async (info) => {
    if (info.Organizations?.length) {
        adminStore.loadCurrentOrganizations(info.Organizations);
    }
    const orgMap = adminStore.getMapOf('organization');
    const orgIdsFromLinks = [...new Set((info.UserOrganizations || [])
            .map((link) => link?.organizationID)
            .filter((id) => typeof id === 'string' && id.length > 0))];
    for (const orgId of orgIdsFromLinks) {
        if (!orgMap.get(orgId)) {
            try {
                await adminStore.load('organization', orgId, STORE_LOAD_FRESH);
            }
            catch (error) {
                console.log(`Failed to load organization ${orgId}`, error);
            }
        }
    }
    const linkedOrganizations = orgIdsFromLinks
        .map((id) => orgMap.get(id))
        .filter(isUsableOrganization);
    if (linkedOrganizations.length > 0) {
        return linkedOrganizations;
    }
    return [...orgMap.values()].filter(isUsableOrganization);
};
const resolveCurrentOrganization = (organizations, preferredOrganizationID) => {
    if (organizations.length === 0)
        return undefined;
    const storedOrgId = preferredOrganizationID || (typeof localStorage !== 'undefined' ? localStorage.getItem('currentOrganizationID') : null);
    if (storedOrgId) {
        const preferred = organizations.find((org) => org.id === storedOrgId);
        if (preferred)
            return preferred;
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('currentOrganizationID');
        }
    }
    return organizations[0];
};
export const getUserInfo = async (preferredOrganizationID, preferredPortalID) => {
    try {
        const result = await getGQLAPI().graphql({
            query: userInfo,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        });
        if (!result.data || !result.data.userInfo) {
            throw Error('no data returned');
        }
        const info = result.data.userInfo;
        await adminStore.getOrLoad('user', info.Internal.id);
        if (!info.UserOrganizations || !info.UserOrganizations.length) {
            adminStore.setStatus('error');
            adminStore.setLoadMessage('You have successfully logged in but have not be granted access to any organizations.  Contact your administrator.');
            return false;
        }
        if (info.UserAttributes) {
            const email = info.UserAttributes.find((attr) => attr.Name === 'email');
            adminStore.setCurrentUser(info.Internal.id || '', email.Value || '');
            // console.log('Email', email.Value);
        }
        if (!info.Internal) {
            throw Error('Missing user record in userInfo response');
        }
        if (info.UserOrganizations) {
            adminStore.loadCurrentUserOrganizations(info.UserOrganizations);
        }
        const accessibleOrganizations = await loadAccessibleOrganizations(info);
        const currentOrganization = resolveCurrentOrganization(accessibleOrganizations, preferredOrganizationID);
        if (!currentOrganization) {
            adminStore.setStatus('error');
            adminStore.setLoadMessage('Your account is linked to organizations that could not be loaded. '
                + 'They may have been removed from QA. Contact your administrator.');
            return false;
        }
        await adminStore.setCurrentOrganization(currentOrganization);
        const portalID = preferredPortalID || localStorage.getItem('currentPortalID');
        if (portalID) {
            adminStore.setCurrentPortal(adminStore.getOrLoad('portal', portalID));
        }
        await adminStore.getCustomerInfo(false);
        if (adminStore.currentPortal && adminStore.currentCustomer) {
            await adminStore.getLearningBoard(adminStore.currentPortal, adminStore.currentCustomer);
        }
        return true;
    }
    catch (error) {
        console.log('Error on getUserInfo', error);
        adminStore.setStatus('error');
        adminStore.setLoadMessage('Failed to load your account. Please reload and try again.');
        return false;
    }
};
/**
 * Used to ensure admin session is initialized before doing business functions.
 * Currently includes get the current user and loading the organization and
 * available repositories.  Can be called multiple times - init will only happen once.
 */
export const initAdminSession = async (preferredOrganizationID) => {
    // console.log('InitAdminSession called')
    if (adminStore.status === 'ready') {
        // console.log('  -- Session ready');
        return;
    }
    if (adminStore.status === 'not_loaded') {
        adminStore.setStatus('loading');
        // console.log('  -- Loading');
        const sessionReady = await getUserInfo(preferredOrganizationID);
        if (sessionReady) {
            await adminStore.getEnvironmentConfig();
        }
        if (adminStore.status !== 'error') {
            adminStore.setStatus('ready');
        }
        console.log('  -- Finished load');
    }
};
export const clearAdminSession = async () => {
    // TODO - clear out data
    adminStore.setStatus('not_loaded');
    Auth.signOut().then(() => {
        window.location.reload();
    });
};
// gets called from index.tsx to make connection - for admin
export const configureClient = (awsExports, preferredOrganizationID) => {
    console.log('Listening on Hub');
    Hub.listen(/.*/, async (data) => {
        // console.log('Hub message: ', data);
        if (data.channel === 'UI Auth') {
            if (data.payload.message === 'signedin') {
                console.log('Signed in');
                await setAuthToken();
                await initAdminSession(preferredOrganizationID);
            }
        }
        else if (data.channel === 'auth') {
            console.log('Auth', data);
            if (data.payload.event === 'configured') {
                console.log('Auth Configured Event');
            }
            if (data.payload.event === 'tokenRefresh') {
                console.log('Token Refresh');
                await refreshAuthToken();
            }
            else {
                await setAuthToken();
                await initAdminSession(preferredOrganizationID);
            }
        }
    });
    console.log('Amplify.configure client');
    Auth = ClientAuth;
    setGQLAPI(API);
    // pulls the environment from the content_delivery_bucket name
    const bucketEnv = awsExports.aws_user_files_s3_bucket?.match(/.*-(\w+)/)?.[1];
    if (bucketEnv) {
        adminStore.setAmplifyEnvironment(bucketEnv);
    }
    Amplify.configure(awsExports);
};
//# sourceMappingURL=AdminStore.js.map